import { useState, useRef, useEffect, useCallback, type JSX } from 'react';
import { useSearchParams } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';
import rehypeSanitize from 'rehype-sanitize';
import { CodeBlock } from '../components/CodeBlock.js';
import { sanitizeAtlassianWiki } from '../lib/atlassian-markup.js';
import { readQuestionParam } from '../lib/agent-deep-link.js';
import {
  ATTACH_LIMITS_HINT,
  extractedTextError,
  rawSizeError,
  type AttachmentKind,
} from '../lib/chat-attachment-limits.js';
import {
  BookTextIcon,
  AlertTriangleIcon,
  XIcon,
  ExternalLinkIcon,
  FileTextIcon,
  ImageIcon,
  FileIcon,
  CopyIcon,
  CheckIcon,
  ThumbsUpIcon,
  ThumbsDownIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  HistoryIcon,
  ArrowForwardIcon,
  AttachFileIcon,
  BrainIcon,
  StopCircleIcon,
  TuneIcon,
  FilesIcon,
  FlagIcon,
  FlagFilledIcon,
  type LucideIcon,
} from '../components/icons.js';
import * as XLSX from 'xlsx';
import { Layout } from '../components/Layout.js';
import { ChatHistoryRail } from '../components/ChatHistoryRail.js';
import { DownloadMenu } from '../components/DownloadMenu.js';
import { CanvasPane } from '../components/CanvasPane.js';
import { CanvasStarterPicker } from '../components/CanvasStarterPicker.js';
import type { CanvasStarterTemplate } from '@kennisbank/shared';
import { isCanvasTranscriptMarker } from '@kennisbank/shared';
import { usePersistedState } from '../hooks/usePersistedState.js';
import { apiGet, apiPost, ApiError } from '../api/client.js';
import type { AgentCitation, ChatMessageFeedback, SourceFlagReason } from '@kennisbank/shared';
import { useAuth } from '../auth/AuthProvider.js';

/**
 * Resolve `attachment://<hash>` markdown image URLs to the backend proxy
 * route (`/api/v1/attachments/<hash>`). Anything else is left untouched
 * so external links in agent answers continue to work.
 */
/**
 * Build the right URL for a chunk's source-file link. Confluence- and
 * Jira-ingested filenames don't have a canonical .md to download — there
 * is none in GCS — so we link to the source-of-truth page on the Atlassian
 * site instead. Everything else routes to the existing /api/v1/agent/sources
 * file proxy (which serves a signed GCS URL for PDFs/uploads/etc.).
 *
 * Filename shapes:
 *   - Confluence: `confluence-{pageId}-{slug}.md` (pageId is digits)
 *   - Jira:       `jira-{key-lower}.md` (e.g. jira-ok-85.md → OK-85)
 */
const ATLASSIAN_BASE_HOST = 'onenebula.atlassian.net';
function sourceFileHref(filename: string): string {
  const conf = /^confluence-(\d+)-/.exec(filename);
  if (conf && conf[1]) {
    return `https://${ATLASSIAN_BASE_HOST}/wiki/pages/viewpage.action?pageId=${conf[1]}`;
  }
  const jira = /^jira-([a-z0-9_-]+)\.md$/i.exec(filename);
  if (jira && jira[1]) {
    return `https://${ATLASSIAN_BASE_HOST}/browse/${jira[1].toUpperCase()}`;
  }
  return `/api/v1/agent/sources/${encodeURIComponent(filename)}`;
}

/**
 * Citation deep-link router. Routes a training-video citation to the
 * Video Library detail page with `?t=` so the player seeks straight to
 * the cited moment. Falls back to sourceFileHref for everything else.
 *
 * The sourceType / videoId / startSeconds fields are emitted by the
 * Python agent when the cited chunk came from the video ingest
 * pipeline (see agents/knowledge-agent/src/agent.py _build_citations).
 */
function citationHref(citation: {
  sourceType?: 'document' | 'video' | 'code' | 'tool';
  videoId?: string;
  startSeconds?: number;
  sourceFile?: string;
  title?: string;
}): string {
  if (citation.sourceType === 'video' && citation.videoId) {
    const t = Math.floor(citation.startSeconds ?? 0);
    return `/videos/${citation.videoId}?t=${t}`;
  }
  const filename = citation.sourceFile ?? `${citation.title ?? ''}.pdf`;
  return sourceFileHref(filename);
}

function resolveAttachmentUrl(href?: string): string {
  if (!href) return '';
  if (href.startsWith('attachment://')) {
    const hash = href.slice('attachment://'.length);
    if (/^[a-f0-9]{64}$/.test(hash)) return `/api/v1/attachments/${hash}`;
  }
  return href;
}

/**
 * Markdown renderer used for citation fragments + (optionally) answer
 * bodies. Allows images so Confluence screenshots embedded as
 * `![caption](attachment://hash)` render inline. Defensive defaults:
 * lazy-load, no referrer, max-width:100% so a wide screenshot can't blow
 * out the citation card. TODO(/documenten): same renderer when document
 * preview lands.
 */
function CitationMarkdown({ text }: { text: string }): JSX.Element {
  return (
    <ReactMarkdown
      // react-markdown v9 default urlTransform strips unknown schemes
      // (attachment://). Resolve up front so the safelist accepts it.
      urlTransform={(uri: string) => resolveAttachmentUrl(uri)}
      remarkPlugins={[remarkGfm]}
      // rehype-raw renders inline HTML (e.g. legacy `<br/>` baked into
      // older agent answers). rehype-sanitize runs after to strip
      // script/iframe/style and any other dangerous tags. Trade-off
      // documented in CLAUDE.md — LLM output trust boundary.
      rehypePlugins={[rehypeRaw, rehypeSanitize]}
      components={{
        code: CodeBlock,
        img: ({ src, alt }) => (
          <img
            src={typeof src === 'string' ? src : ''}
            alt={alt ?? ''}
            loading="lazy"
            referrerPolicy="no-referrer"
            style={{ maxWidth: '100%', height: 'auto', borderRadius: 6 }}
          />
        ),
        a: ({ href, children }) => (
          <a href={href} target="_blank" rel="noopener noreferrer">
            {children}
          </a>
        ),
      }}
    >
      {sanitizeAtlassianWiki(text)}
    </ReactMarkdown>
  );
}

/**
 * Markdown renderer for Knowledge Agent answer bodies. Pre-processes
 * inline `[chunkId: …]` markers into hash-anchor links so react-markdown
 * can surface them as numbered superscripts. Code blocks render via the
 * ChatGPT-style CodeFenceCard.
 */
function AnswerMarkdown({
  text,
  citations,
  onCiteClick,
}: {
  text: string;
  citations: AgentCitation[];
  /** Called when the reader clicks an inline `[N]` superscript pill. The
   *  parent uses it to expand the per-turn source list and scroll the
   *  matching card into view. */
  onCiteClick?: (chunkId: string) => void;
}): JSX.Element {
  const indexByChunkId = new Map<string, number>();
  const indexByFilename = new Map<string, number>();
  citations.forEach((c, i) => {
    indexByChunkId.set(c.chunkId, i + 1);
    if (c.sourceFile) indexByFilename.set(c.sourceFile.toLowerCase(), i + 1);
    indexByFilename.set(c.title.toLowerCase(), i + 1);
  });
  // Match both supported citation marker shapes:
  //   [chunkId: <id>(, <id>)*]  — canonical format from the system prompt
  //   [attached: <filename>(, <hex>)?] — the user-upload format the model
  //     sometimes mis-uses for retrieved corpus chunks (it concatenates the
  //     adjacent `document:` + `chunkId:` lines from the fragment block into
  //     a hybrid marker). We resolve those by hex first, then by filename;
  //     unresolved markers are stripped silently rather than leaked to the
  //     reader as `[attached: foo.pdf, 8306a…]`. User-feedback fix
  //     2026-06-10. Root-cause prompt fix is in system.md.
  const HEX_TOKEN = /^[0-9a-f]{16,}$/i;
  const processed = sanitizeAtlassianWiki(text).replace(
    /\[(chunkId|attached):\s*([^\]]+)\]/g,
    (_match, kind: string, raw: string) => {
      const tokens = raw
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean);
      const matchedIds: string[] = [];
      const numbers: number[] = [];
      for (const token of tokens) {
        // Direct chunkId hit — works for both marker shapes.
        const direct = indexByChunkId.get(token);
        if (direct !== undefined) {
          matchedIds.push(token);
          numbers.push(direct);
          continue;
        }
        // For `[attached: ...]` only, the leading token may be a filename.
        // Look it up by sourceFile / title. Hex-shaped tokens never match a
        // filename so this is a no-op for `[chunkId: ...]`.
        if (kind === 'attached' && !HEX_TOKEN.test(token)) {
          const byName = indexByFilename.get(token.toLowerCase());
          if (byName !== undefined) {
            // Look up the chunkId at that 1-based slot for the anchor.
            const cite = citations[byName - 1];
            if (cite) {
              matchedIds.push(cite.chunkId);
              numbers.push(byName);
            }
          }
        }
      }
      if (numbers.length === 0) return '';
      const label = numbers.map((n) => `\\[${n}\\]`).join('');
      return `[${label}](#cite-${matchedIds.join('-')})`;
    },
  );
  return (
    <ReactMarkdown
      urlTransform={(uri: string) => resolveAttachmentUrl(uri)}
      remarkPlugins={[remarkGfm]}
      // See CitationMarkdown above for the rationale.
      rehypePlugins={[rehypeRaw, rehypeSanitize]}
      components={{
        code: CodeBlock,
        a: ({ href, children }) => {
          if (typeof href === 'string' && href.startsWith('#cite-')) {
            // `#cite-<id1>-<id2>…` — the first id is enough to expand the
            // panel and scroll. The matching CitationRow then carries the
            // brief ring-highlight. Cursor stays a pointer; default
            // hash-navigation is suppressed so the URL doesn't change.
            const firstId = href.slice('#cite-'.length).split('-')[0] ?? '';
            return (
              <sup className="ml-0.5 align-baseline text-[10px] font-medium text-brand-700">
                <a
                  href={href}
                  onClick={(e) => {
                    e.preventDefault();
                    if (onCiteClick && firstId) onCiteClick(firstId);
                  }}
                  className="cursor-pointer hover:underline"
                >
                  {children}
                </a>
              </sup>
            );
          }
          return (
            <a href={href} target="_blank" rel="noopener noreferrer">
              {children}
            </a>
          );
        },
        img: ({ src, alt }) => (
          <img
            src={typeof src === 'string' ? src : ''}
            alt={alt ?? ''}
            loading="lazy"
            referrerPolicy="no-referrer"
            style={{ maxWidth: '100%', height: 'auto', borderRadius: 6 }}
          />
        ),
      }}
    >
      {processed}
    </ReactMarkdown>
  );
}

const IMAGE_EXTENSIONS = /\.(jpe?g|jfif|png|webp|gif)$/i;
const TEXT_DOC_EXTENSIONS = /\.(pdf|odt|docx)$/i;

function pickSourceIcon(filename: string): LucideIcon {
  if (IMAGE_EXTENSIONS.test(filename)) return ImageIcon;
  if (TEXT_DOC_EXTENSIONS.test(filename)) return FileTextIcon;
  return FileIcon;
}

/**
 * Slice 3a Knowledge Agent chat page.
 *
 * Multi-turn conversation surface — each ask appends a new question/answer
 * pair below the previous ones, so the operator can keep drilling on the
 * same topic. Per turn: a single answer card holds the prose, the source
 * citations (compact rows) and a feedback footer in one visual container.
 *
 * Error UX maps backend status codes to Dutch copy per the slice-3a plan.
 */

/** Frontend mirror of shared/src/ring2-sources.ts RING2_SOURCE_LABELS.
 * Kept inline rather than imported because the chat surface stays a
 * pure presentational layer over the JSON the agent returns. */
const RING2_SOURCE_LABELS: Record<string, string> = {
  nhg: 'NHG',
  trimbos: 'Trimbos',
  thuisarts: 'Thuisarts.nl',
  ggz_standaarden: 'GGZ-standaarden',
  rivm: 'RIVM',
};

interface AskResponse {
  requestId: string;
  /** Slice 3b chat-history wedge — present on every successful response. */
  chatId: string;
  /** Assistant message id — needed for per-turn feedback + source-flag
   *  without waiting for chat-history rehydration. */
  messageId?: string;
  answer: string;
  citations: AgentCitation[];
  outcome: 'success' | 'low_confidence_rewrite' | 'empty_retrieval' | 'refused';
}

interface AskStreamHandlers {
  /** Called for each token chunk the agent emits while generating.
   * `partial` is the cumulative answer text so far — render it directly,
   * no append needed. */
  onDelta(partial: string): void;
}

/**
 * Slice 7 — streaming-aware /ask client. Sends Accept for both content
 * types so the backend can pick (env-flag-controlled). Two response
 * shapes:
 *   - `text/event-stream` → consume the stream, fire `onDelta` for each
 *     chunk, resolve with the final AskResponse on `done`.
 *   - `application/json` → parse, fire one `onDelta(answer)` for state-
 *     machine consistency, resolve with the parsed body.
 *
 * Throws ApiError on non-2xx (parsed from the error envelope). The
 * `signal` param wires the existing AbortController so the user-side
 * "Cancel" button kills both shapes identically.
 */
type AnswerMode =
  | 'quick'
  | 'deep_developer'
  | 'deep_marketing'
  | 'deep_customer_success'
  | 'deep_quality_analyst'
  | 'deep_legal'
  | 'deep_business_analyst';

type CitationDepth = 'concise' | 'balanced' | 'thorough';

const CITATION_DEPTH_OPTIONS: { value: CitationDepth; label: string; hint: string }[] = [
  { value: 'concise', label: 'Concise', hint: 'Faster, ~5 sources' },
  { value: 'balanced', label: 'Balanced', hint: '~10 sources' },
  { value: 'thorough', label: 'Thorough', hint: 'Slower, ~20 sources' },
];

// kb_admin-only model picker. Wire-level identifiers mirror the shared
// `AgentModelId` enum. Non-admin users never see this control — the
// dropdown is rendered conditionally on `user.role === 'kb_admin'`.
type AgentModelId =
  | 'gemini-2.5-pro'
  | 'gemini-3.5-flash'
  | 'claude-opus-4-7'
  | 'claude-sonnet-latest';

const DEFAULT_MODEL_ID: AgentModelId = 'gemini-2.5-pro';

const MODEL_OPTIONS: { value: AgentModelId; label: string }[] = [
  { value: 'gemini-2.5-pro', label: 'Gemini 2.5 Pro (default)' },
  { value: 'gemini-3.5-flash', label: 'Gemini 3.5 Flash' },
  { value: 'claude-opus-4-7', label: 'Claude Opus 4.7' },
  { value: 'claude-sonnet-latest', label: 'Claude Sonnet 4.6' },
];

// Models only kb_admins may pick. The picker is open to all users, but Opus is
// expensive with a scarce Vertex quota, so it stays admin-only. Keep in sync
// with the backend's ADMIN_ONLY_MODELS (agent.ts).
const ADMIN_ONLY_MODELS = new Set<AgentModelId>(['claude-opus-4-7']);
function canPickModel(modelId: AgentModelId, isAdmin: boolean): boolean {
  return isAdmin || !ADMIN_ONLY_MODELS.has(modelId);
}

// Compact label shown on the per-message badge. Strips marketing decoration
// so chips stay one-line on narrow viewports. Includes legacy gemini-2.5-flash
// + flash-lite entries so badges on OLD chat messages (persisted with those
// model IDs) still render with a readable label rather than the raw id.
const MODEL_BADGE_LABELS: Record<string, string> = {
  'gemini-2.5-pro': 'Gemini 2.5 Pro',
  'gemini-3.5-flash': 'Gemini 3.5 Flash',
  'gemini-2.5-flash': 'Gemini 2.5 Flash (legacy)',
  'gemini-2.5-flash-lite': 'Gemini 2.5 Flash-Lite (legacy)',
  'claude-opus-4-7': 'Claude Opus 4.7',
  'claude-sonnet-latest': 'Claude Sonnet 4.6',
};

function modelBadgeLabel(modelId: string | undefined): string | null {
  if (!modelId) return null;
  return MODEL_BADGE_LABELS[modelId] ?? modelId;
}

const ANSWER_MODE_STORAGE_KEY = 'kb.agent.answerMode';

/**
 * Poll the single-chat endpoint at 1s, 2s, 4s, 8s (cumulative ~15s) and
 * bump the rail's refreshKey when the title flips to "generated". Stops
 * early on generated/failed/missing. Replaces the old 0ms + 2.5s blind
 * refetches that frequently lost the Gemini Flash race.
 */
async function pollUntilTitled(
  chatId: string,
  bumpRefresh: (next: (k: number) => number) => void,
): Promise<void> {
  // Single GET against the per-chat endpoint per tick; we only bump the
  // sidebar refresh key when the title transitions to a terminal state, so
  // each submit costs at most ONE chat-list refetch instead of one per tick.
  // Previous impl bumped on every tick which 5x'd the list-refresh traffic
  // and contributed to the global-rate-limit storm seen on 2026-05-18.
  const delays = [1000, 2000, 4000, 8000];
  for (const delay of delays) {
    await new Promise((resolve) => window.setTimeout(resolve, delay));
    // Skip the tick when the tab is backgrounded — title-gen state will
    // be picked up by the next refreshKey bump (or on tab focus). Cuts
    // /api/v1/chats/:id traffic from idle tabs to zero.
    if (typeof document !== 'undefined' && document.hidden) {
      continue;
    }
    try {
      const res = await apiGet<{ chat: { titleStatus: string } }>(`/api/v1/chats/${chatId}`);
      const status = res.chat.titleStatus;
      if (status === 'generated' || status === 'failed') {
        bumpRefresh((k) => k + 1);
        return;
      }
    } catch {
      // Transient — keep polling on the remaining schedule.
    }
  }
  // Timed out without a terminal status — one final bump so the rail picks
  // up the "still pending" state and the >5min fallback kicks in if needed.
  bumpRefresh((k) => k + 1);
}

async function askStreaming(
  payload: {
    query: string;
    documentTitles?: string[];
    chatId?: string;
    clientRequestId?: string;
    mode?: AnswerMode;
    citationDepth?: CitationDepth;
    modelId?: AgentModelId;
    attachment?: {
      filename: string;
      mimeType: string;
      contentText: string;
      sizeBytes: number;
    };
  },
  handlers: AskStreamHandlers,
  signal?: AbortSignal,
): Promise<AskResponse> {
  const res = await fetch('/api/v1/agent/ask', {
    method: 'POST',
    credentials: 'include',
    headers: {
      'content-type': 'application/json',
      // Both shapes — backend picks based on env flag. When streaming is
      // off, backend ignores the SSE part and returns JSON; we still
      // surface a single delta so the typing indicator hand-off is uniform.
      accept: 'text/event-stream, application/json',
    },
    body: JSON.stringify(payload),
    signal,
  });

  if (!res.ok) {
    // Backend error bodies are `{ error, message }` (middleware/error-handler
    // + rate-limit). Reading `code` here used to turn every failure into
    // 'unknown', so ERROR_COPY never matched.
    const err = (await res.json().catch(() => ({}))) as {
      error?: string;
      code?: string;
      message?: string;
    };
    throw new ApiError(res.status, err.error ?? err.code ?? 'unknown', err.message ?? '');
  }

  const ct = res.headers.get('content-type') ?? '';
  if (!ct.includes('text/event-stream')) {
    // Legacy JSON fallback — fire one delta so the UI still gets a
    // single render-tick before the final swap.
    const body = (await res.json()) as AskResponse;
    handlers.onDelta(body.answer);
    return body;
  }

  // chatId comes via custom header on streaming responses (SSE payload
  // doesn't carry it — it's the backend's domain). Set BEFORE we start
  // consuming the stream so it's available even if the stream truncates.
  const chatIdHeader = res.headers.get('x-chat-id') ?? '';

  // SSE — read the body as a stream. Buffer across chunk boundaries
  // (a single SSE event may span multiple read() chunks at high rates).
  const reader = res.body!.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  let cumulativeText = '';
  let cumulativeMessageId: string | undefined;
  let final: AskResponse | null = null;

  // Wire the abort signal to the reader. The `signal` was already passed
  // to fetch() above, but once we've started consuming the body stream
  // the signal stops affecting the reader. Without this listener, the
  // Stop button visibly fires (UI state flips) but the SSE loop keeps
  // pulling deltas server-side until the agent finishes — wastes tokens
  // and feels broken. Calling reader.cancel() also propagates the
  // close to Node, which fires req.on('close') in agent.ts to abort
  // the upstream Python call. Bug fixed 2026-05-17.
  const onAbort = (): void => {
    void reader.cancel('aborted-by-user').catch(() => {
      /* swallow — racing with a normal completion is harmless */
    });
  };
  if (signal) {
    if (signal.aborted) onAbort();
    else signal.addEventListener('abort', onAbort, { once: true });
  }

  try {
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      let nlIdx: number;
      while ((nlIdx = buffer.indexOf('\n\n')) !== -1) {
        const block = buffer.slice(0, nlIdx);
        buffer = buffer.slice(nlIdx + 2);
        for (const line of block.split('\n')) {
          if (!line.startsWith('data: ')) continue;
          const payload = JSON.parse(line.slice('data: '.length));
          if (payload.type === 'delta') {
            cumulativeText += payload.text;
            handlers.onDelta(cumulativeText);
          } else if (payload.type === 'done') {
            final = {
              requestId: payload.requestId,
              chatId: chatIdHeader,
              answer: payload.answer,
              citations: payload.citations,
              outcome: payload.outcome,
            };
          } else if (payload.type === 'meta') {
            // Backend emits this AFTER persisting the assistant message
            // so we can wire per-turn feedback without a chat-history
            // round-trip. Merge into `final` if it's already set; else
            // stash and merge when `done` lands.
            if (final) {
              final.messageId = payload.messageId;
            } else {
              // Rare ordering — keep the id so the next `done` merges it.
              cumulativeMessageId = payload.messageId;
            }
          } else if (payload.type === 'error') {
            throw new ApiError(500, payload.code ?? 'internal_error', payload.message ?? '');
          }
        }
      }
    }
  } finally {
    if (signal) signal.removeEventListener('abort', onAbort);
  }

  // If the caller cancelled mid-stream, surface as AbortError so the
  // page's catch block runs the same code path it does for any other
  // user-initiated cancel.
  if (signal?.aborted) {
    throw new DOMException('Aborted by user', 'AbortError');
  }

  if (!final) {
    throw new ApiError(500, 'stream_truncated', 'The response was interrupted.');
  }
  // Defensive merge — if `meta` arrived BEFORE `done` (rare; the
  // backend currently writes meta after done, but order isn't
  // contractually guaranteed across network boundaries).
  if (!final.messageId && cumulativeMessageId) {
    final.messageId = cumulativeMessageId;
  }
  return final;
}

interface ChatThreadResponse {
  chat: { chatId: string; title: string };
  messages: Array<{
    messageId: string;
    role: 'user' | 'assistant';
    content: string;
    sources: AgentCitation[];
    /** Set on assistant turns — drives the per-message model badge. */
    modelId?: string;
    createdAt: string;
    feedback?: ChatMessageFeedback;
    /**
     * Per-source accuracy flags this user owns on this assistant message.
     * Keyed by chunkId so the CitationRow can hydrate its Flagged state
     * in O(1). Cross-user flags are never surfaced here.
     */
    sourceFlags?: Record<string, { reason: SourceFlagReason; comment?: string }>;
    /**
     * File uploaded with this turn — only set on user-role messages and
     * only when the user attached something. Bytes live in GCS; backend
     * surfaces filename + sizeBytes for the chip + a download link via
     * `/api/v1/chats/:chatId/messages/:messageId/attachment`.
     */
    attachment?: { filename: string; sizeBytes: number };
  }>;
}

// No `rate_limited` entry on purpose: the backend's 429 message carries the
// real limit and how many minutes until it resets, so it is shown verbatim.
const ERROR_COPY: Record<string, string> = {
  agent_unavailable:
    'The Knowledge Agent is temporarily unavailable. Please try again in a minute.',
  agent_timeout: 'The answer is taking longer than expected. Please try a more specific question.',
  forbidden: 'This feature is only available to authorised users.',
};

const FALLBACK_ERROR = 'Something went wrong. Please try again.';

const EMPTY_RETRIEVAL_HINT =
  'Rephrase your question more specifically, or verify in a primary source.';

interface ChatState {
  question: string;
  answer: AskResponse;
  /** Snapshot of the scope this turn was answered against. */
  scopeTitles: string[] | null;
  /**
   * Model id that produced this turn's answer. Surfaced as a small badge
   * next to the answer block so admins comparing models can tell which
   * provider produced which response. Hydrated from
   * `chats/{id}/messages/{id}.modelId` on chat reload; absent on legacy
   * turns predating the kb_admin model picker.
   */
  modelId?: string;
  /**
   * Assistant message id for this turn — needed to attach per-turn
   * feedback. Populated when hydrating from chat history; absent on the
   * very fresh turn that just streamed in (the `requestId` field carries
   * the message id once the chat-history fetch resolves on the next
   * navigation).
   */
  messageId?: string;
  /**
   * Pre-existing feedback hydrated from chat history. The widget seeds
   * its local state from this on mount.
   */
  initialFeedback?: ChatMessageFeedback;
  /**
   * Per-source accuracy flags hydrated from chat history, keyed by
   * chunkId. Forwarded to each CitationRow so the Flag button can show
   * its "Flagged" state on first paint after navigation.
   */
  initialSourceFlags?: Record<string, { reason: SourceFlagReason; comment?: string }>;
  /**
   * File the user attached to this question. `userMessageId` is set once
   * the chat is rehydrated from history (it's the doc id the
   * download endpoint needs); on the fresh optimistic turn it's absent
   * and the chip renders without a download link.
   */
  attachment?: {
    filename: string;
    sizeBytes: number;
    userMessageId?: string;
  };
}

interface DocumentSummary {
  sourceFile: string;
  documentTitle: string;
  author: string;
}

interface DocumentsResponse {
  documents: DocumentSummary[];
}

interface AttachmentState {
  filename: string;
  mimeType: string;
  contentText: string;
  sizeBytes: number;
}

export function KnowledgeAgentPage(): JSX.Element {
  const { user, flags } = useAuth();
  const isAdmin = user?.role === 'kb_admin';
  // Layout prefs (issue #3 UX pass) — collapse the chat-history rail and
  // resize the canvas/chat split. Persisted across sessions.
  const [historyCollapsed, setHistoryCollapsed] = usePersistedState<boolean>(
    'kb.layout.historyCollapsed',
    false,
  );
  const [canvasWidth, setCanvasWidth] = usePersistedState<number>('kb.layout.canvasWidth', 480);
  const [draggingCanvas, setDraggingCanvas] = useState(false);
  // Whether the active chat has a canvas. Set by CanvasPane via the
  // onCanvasStateChange callback. Drives the "Add to canvas" button on
  // chat answers — invisible until there's a canvas to add to.
  const [canvasExists, setCanvasExists] = useState(false);
  // Bumped after a chat-flow canvas update succeeds so CanvasPane
  // re-fetches its current canvas.
  const [canvasRefreshKey, setCanvasRefreshKey] = useState(0);
  // Per-turn "adding to canvas" guard so the button can show a loading
  // state without blocking the rest of the page. Keyed by messageId.
  const [addingToCanvas, setAddingToCanvas] = useState<Set<string>>(new Set());
  // Next-turn canvas mode toggle on the input (issue #3 — affordance to
  // create or update the canvas with the next ask). null = chat-mode;
  // 'create' = the next ask creates a canvas with `canvasNextStarter`;
  // 'update' = the next ask rewrites the existing canvas. Cleared after
  // submit so the next turn defaults back to chat.
  const [canvasNextMode, setCanvasNextMode] = useState<'create' | 'update' | null>(null);
  const [canvasNextStarter, setCanvasNextStarter] = useState<CanvasStarterTemplate | null>(null);
  const [draft, setDraft] = useState('');
  // Single text-file attachment for the next ask. Cleared after submit so
  // attachments don't accidentally ride along on a follow-up question.
  const [attachment, setAttachment] = useState<AttachmentState | null>(null);
  const [attachError, setAttachError] = useState<string | null>(null);
  const [turns, setTurns] = useState<ChatState[]>([]);
  const [pending, setPending] = useState(false);
  // Slice 7 streaming — accumulates SSE deltas for the in-flight ask.
  // Empty string means "request hasn't received any tokens yet" (still
  // showing the typing indicator); non-empty means "render this as the
  // tentative answer prose." Cleared once the final response lands and
  // turns is updated.
  const [streamingText, setStreamingText] = useState('');
  const [errorCopy, setErrorCopy] = useState<string | null>(null);
  // `showAbort` historically gated a standalone Cancel button under the
  // typing indicator. The compound-input refactor swaps that out for the
  // Ask → Stop primary button, which is gated on `pending` directly. The
  // setter is kept so the existing submit/cancel/hydration code paths
  // don't drift; the value is unread by design.
  const [, setShowAbort] = useState(false);
  // Scope picker state. `null` means "all docs" (default). Once the
  // operator opens the picker and saves a selection, this becomes a
  // list of documentTitles. Persists across turns until cleared.
  const [scopeTitles, setScopeTitles] = useState<string[] | null>(null);
  const [scopePickerOpen, setScopePickerOpen] = useState(false);
  // Four-mode answer-depth picker. Sticky across messages until the user
  // changes it; persisted to localStorage. Quick = current default behaviour.
  const [answerMode, setAnswerMode] = useState<AnswerMode>(() => {
    if (typeof window === 'undefined') return 'quick';
    const stored = window.localStorage.getItem(ANSWER_MODE_STORAGE_KEY);
    if (
      stored === 'quick' ||
      stored === 'deep_developer' ||
      stored === 'deep_marketing' ||
      stored === 'deep_customer_success' ||
      stored === 'deep_quality_analyst' ||
      stored === 'deep_legal' ||
      stored === 'deep_business_analyst'
    ) {
      return stored;
    }
    return 'quick';
  });
  useEffect(() => {
    if (typeof window === 'undefined') return;
    window.localStorage.setItem(ANSWER_MODE_STORAGE_KEY, answerMode);
  }, [answerMode]);
  // Citation-depth knob — session-local. Default Balanced. Not persisted
  // across reloads by design (the spec calls it a per-session control).
  const [citationDepth, setCitationDepth] = useState<CitationDepth>('balanced');
  // Answer-model picker, open to all users (Opus stays admin-only via
  // canPickModel). Default = Gemini 2.5 Pro. Wire payload only includes the
  // field when it deviates from the default — keeps default traffic
  // byte-identical.
  const [modelId, setModelId] = useState<AgentModelId>(DEFAULT_MODEL_ID);
  // Page-level document list — hoisted from ScopePickerModal so the
  // compound input can show "{N} documents in scope" without a duplicate
  // fetch. Same `/api/v1/agent/documents` endpoint the modal already used.
  const [allDocs, setAllDocs] = useState<DocumentSummary[] | null>(null);
  useEffect(() => {
    let alive = true;
    apiGet<DocumentsResponse>('/api/v1/agent/documents')
      .then((d) => {
        if (alive) setAllDocs(d.documents);
      })
      .catch(() => {
        // Silent — the count just won't render. Scope picker has its
        // own error surface for the same endpoint.
      });
    return () => {
      alive = false;
    };
  }, []);
  // Slice 3b chat-history wedge state.
  const [currentChatId, setCurrentChatId] = useState<string | null>(null);
  const [historyRefreshKey, setHistoryRefreshKey] = useState(0);
  const [historyOpen, setHistoryOpen] = useState(false);
  // The user's in-flight question — stays visible above the typing indicator
  // while the agent is still answering. Survives nav-away because we hydrate
  // it from the persisted chat history on mount (see useEffect below).
  const [pendingQuestion, setPendingQuestion] = useState<string | null>(null);
  const abortRef = useRef<AbortController | null>(null);
  const abortTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pollTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const bottomRef = useRef<HTMLDivElement | null>(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const draftInputRef = useRef<HTMLTextAreaElement | null>(null);
  // `?q=` prefill waiting to be applied. Held back while the on-mount chat
  // restore runs, because hydrateFromChat resets the draft when it lands.
  const pendingPrefillRef = useRef<string | null>(null);
  // A counter, not a flag: StrictMode runs the mount effect twice in dev.
  const mountHydrationsRef = useRef(0);
  const [focusDraftToken, setFocusDraftToken] = useState(0);
  const applyPendingPrefill = (): void => {
    const question = pendingPrefillRef.current;
    if (!question || mountHydrationsRef.current > 0) return;
    pendingPrefillRef.current = null;
    // Never clobber a draft that is already there — e.g. the question
    // hydrateFromChat restored after a previous answer failed.
    setDraft((prev) => (prev.trim() ? prev : question));
    setFocusDraftToken((t) => t + 1);
  };

  // ─── chatId persistence + nav-away recovery ────────────────────────────
  // Key the storage by user so a different login on the same browser
  // doesn't see the previous user's chat. Falls back to a generic key
  // when no auth context is around (dev / first paint).
  const CHAT_ID_STORAGE_KEY = 'agent.activeChatId';
  useEffect(() => {
    if (currentChatId) {
      sessionStorage.setItem(CHAT_ID_STORAGE_KEY, currentChatId);
    } else {
      sessionStorage.removeItem(CHAT_ID_STORAGE_KEY);
    }
  }, [currentChatId]);

  // Auto-scroll the latest turn into view whenever a new answer arrives or
  // we transition into the pending/error state. `behavior: smooth` keeps
  // the long-running case (cold-start agent) feeling responsive.
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
  }, [turns.length, pending, errorCopy]);

  const submit = async (): Promise<void> => {
    const rawQuestion = draft.trim();
    if (!rawQuestion || pending) return;

    // /qa slash command. Two shapes:
    //   /qa PROJ-1234 <question>             → bare key
    //   /qa https://x.atlassian.net/browse/PROJ-1234 <question>  → Jira URL
    //   /qa <question>                        → flip mode only, no ticket
    // The mode flip is sticky (writes through setAnswerMode + localStorage)
    // so subsequent turns stay in QA mode until the user picks another pill.
    let question = rawQuestion;
    let perTurnJiraTicketKey: string | undefined;
    let perTurnJiraSearch: string | undefined;
    let perTurnMode: AnswerMode | undefined;

    // /jira <terms> — free-text Jira search. Backend runs a JQL full-text
    // search and injects the matching ticket list so the agent can answer
    // "which tickets cover X". Doesn't force a mode change.
    const jiraSearchMatch = /^\/jira\b\s*(.*)$/is.exec(rawQuestion);
    if (jiraSearchMatch) {
      const terms = (jiraSearchMatch[1] ?? '').trim();
      if (terms.length === 0) {
        setErrorCopy(
          'Type `/jira <search terms>` to find tickets, e.g. `/jira currency conversion wan reports`.',
        );
        return;
      }
      perTurnJiraSearch = terms;
      question = terms;
    }

    const slashMatch = !jiraSearchMatch ? /^\/qa\b\s*(.*)$/is.exec(rawQuestion) : null;
    if (slashMatch) {
      perTurnMode = 'deep_quality_analyst';
      const remainder = (slashMatch[1] ?? '').trim();
      if (remainder.length === 0) {
        // Bare "/qa" with nothing after — surface help instead of submitting.
        setErrorCopy(
          'Type `/qa PROJ-1234 <optional question>`, paste a Jira URL like `/qa https://onenebula.atlassian.net/browse/PROJ-1234`, or `/qa <question>` to ask in QA mode without a ticket.',
        );
        return;
      }
      // Find a Jira key anywhere in the remainder — bare or embedded in a URL
      // like https://<host>/browse/PROJ-1234. Falls back to QA-mode-only if no
      // key is present.
      const TICKET_KEY_RE = /[A-Z][A-Z0-9_]+-\d+/;
      const keyMatch = TICKET_KEY_RE.exec(remainder);
      if (keyMatch) {
        perTurnJiraTicketKey = keyMatch[0];
        // Strip both forms (the URL if there was one, and the bare key) so
        // the question text the agent sees is the user's actual ask, not a
        // mix of URL noise and prose.
        const cleaned = remainder
          .replace(/https?:\/\/\S*\/browse\/[A-Z][A-Z0-9_]+-\d+\S*/gi, '')
          .replace(TICKET_KEY_RE, '')
          .replace(/\s+/g, ' ')
          .trim();
        question =
          cleaned.length > 0
            ? cleaned
            : `Produce a QA approach and risk-prioritised test plan for ${perTurnJiraTicketKey}.`;
      } else {
        // No key found — QA mode on the prose alone.
        question = remainder;
      }
      if (answerMode !== perTurnMode) {
        setAnswerMode(perTurnMode);
      }
    }

    if (pending) return;
    setErrorCopy(null);
    setPending(true);
    setPendingQuestion(question);
    setShowAbort(false);

    abortRef.current = new AbortController();
    // Show Cancel immediately rather than after 5s — the user can choose
    // when to bail. Previously a 5s grace period kept the typing indicator
    // pristine, but it also stranded users whose request had genuinely
    // hung (or whose page reloaded mid-flight and recovered a pending
    // state without an abort controller).
    setShowAbort(true);
    abortTimerRef.current = null;

    try {
      const askPayload: {
        query: string;
        documentTitles?: string[];
        chatId?: string;
        clientRequestId?: string;
        mode?: AnswerMode;
        citationDepth?: CitationDepth;
        modelId?: AgentModelId;
        attachment?: AttachmentState;
        jiraTicketKey?: string;
        jiraSearch?: string;
        canvasMode?: 'create' | 'update';
        starterTemplate?: CanvasStarterTemplate;
      } = {
        query: question,
      };
      // Canvas next-turn dispatch (issue #3 input toggle). When the user
      // has activated the Canvas button, this turn goes to the canvas
      // flow instead of the regular chat answer. The starter template
      // ships only on the create path; update reuses the existing canvas
      // structure.
      if (canvasNextMode === 'create') {
        askPayload.canvasMode = 'create';
        if (canvasNextStarter) {
          askPayload.starterTemplate = canvasNextStarter;
        }
      } else if (canvasNextMode === 'update') {
        askPayload.canvasMode = 'update';
      }
      if (perTurnJiraTicketKey) {
        askPayload.jiraTicketKey = perTurnJiraTicketKey;
      }
      if (perTurnJiraSearch) {
        askPayload.jiraSearch = perTurnJiraSearch;
      }
      if (scopeTitles && scopeTitles.length > 0) {
        askPayload.documentTitles = scopeTitles;
      }
      if (currentChatId) {
        askPayload.chatId = currentChatId;
      } else {
        // Stable idempotency token for this "new chat" submit. Captured at
        // the moment of the click so React Strict Mode double-effect, quick
        // double-clicks, and page reloads that race the X-Chat-Id header
        // all collapse onto the same chat doc instead of creating duplicates.
        askPayload.clientRequestId = crypto.randomUUID();
      }
      // Only send mode when it's not the quick default — keeps the wire
      // byte-identical to pre-feature behaviour on the quick path. The
      // per-turn override from /qa takes precedence so the slash command
      // works even before the React state flip lands.
      const effectiveMode = perTurnMode ?? answerMode;
      if (effectiveMode !== 'quick') {
        askPayload.mode = effectiveMode;
      }
      // Send citationDepth only when it deviates from the default — keeps
      // the wire byte-identical to pre-feature behaviour on the balanced path.
      if (citationDepth !== 'balanced') {
        askPayload.citationDepth = citationDepth;
      }
      // Send modelId when the user moved off the default AND may pick it
      // (Opus stays admin-only; backend re-checks). Keeps the wire clean and
      // the model split observable in audit logs.
      if (modelId !== DEFAULT_MODEL_ID && canPickModel(modelId, isAdmin)) {
        askPayload.modelId = modelId;
      }
      if (attachment) {
        askPayload.attachment = attachment;
      }
      // Slice 7 — streaming-aware ask. Server picks JSON vs SSE based on
      // its env flag; client always sends both Accept types so we work
      // identically against either response shape.
      const response = await askStreaming(
        askPayload,
        { onDelta: (partial) => setStreamingText(partial) },
        abortRef.current?.signal,
      );
      setTurns((prev) => [
        ...prev,
        {
          question,
          answer: response,
          scopeTitles: scopeTitles ?? null,
          // Capture messageId so per-turn feedback + source-flag work
          // immediately, without needing a chat-history refetch. Backend
          // emits the assistant message id in the SSE `meta` frame (or
          // alongside the JSON response).
          ...(response.messageId ? { messageId: response.messageId } : {}),
          // Badge optimistically with the chosen id when the user picked a
          // non-default model they're allowed to use, until the chat-history
          // reload picks up the server-recorded value.
          ...(modelId !== DEFAULT_MODEL_ID && canPickModel(modelId, isAdmin) ? { modelId } : {}),
          // Optimistic attachment record so the question bubble renders a
          // chip immediately. `userMessageId` is filled in when the chat
          // is later rehydrated from history — until then the chip has
          // no download link (the user still has the file locally).
          ...(attachment
            ? {
                attachment: { filename: attachment.filename, sizeBytes: attachment.sizeBytes },
              }
            : {}),
        },
      ]);
      setCurrentChatId(response.chatId);
      // Bump the rail so the new row appears immediately, then poll the
      // single chat endpoint until titleStatus="generated" or ~15s elapse.
      // Backoff schedule: 1s, 2s, 4s, 8s (cumulative ~15s). Replaces the
      // old 0ms + 2.5s refetches which often missed the Gemini Flash call.
      setHistoryRefreshKey((k) => k + 1);
      void pollUntilTitled(response.chatId, (key) => setHistoryRefreshKey(key));
      setDraft('');
      setAttachment(null);
      setAttachError(null);
      // Reset canvas next-turn mode after a successful send — the dispatch
      // is single-shot (the user can opt in again on the next turn).
      // Also bump the canvas refresh key when the turn went canvas-bound
      // so CanvasPane re-fetches the newly written version.
      if (canvasNextMode) {
        setCanvasRefreshKey((k) => k + 1);
      }
      setCanvasNextMode(null);
      setCanvasNextStarter(null);
    } catch (err) {
      if (err instanceof ApiError) {
        setErrorCopy(ERROR_COPY[err.code] ?? (err.message || FALLBACK_ERROR));
      } else if ((err as Error).name === 'AbortError') {
        setErrorCopy('Request cancelled.');
      } else {
        setErrorCopy(FALLBACK_ERROR);
      }
    } finally {
      setPending(false);
      setPendingQuestion(null);
      setStreamingText('');
      setShowAbort(false);
      if (abortTimerRef.current) clearTimeout(abortTimerRef.current);
      abortTimerRef.current = null;
      abortRef.current = null;
    }
  };

  const cancel = (): void => {
    // Abort the in-flight fetch when one exists (normal cancel path).
    abortRef.current?.abort();
    // Hard-reset the pending state too — covers the case where the page
    // was reloaded mid-flight and there's no abort controller to fire,
    // but the UI is still in "thinking..." mode from hydration polling.
    // Without this branch the input stays disabled forever.
    stopPolling();
    setPending(false);
    setPendingQuestion(null);
    setStreamingText('');
    setShowAbort(false);
    if (abortTimerRef.current) clearTimeout(abortTimerRef.current);
    abortTimerRef.current = null;
    abortRef.current = null;
  };

  // Slice 3b chat-history wedge: reset to a fresh blank chat. Matches the
  // /office-hours D3A decision (preserve slice-3a behavior; returning user
  // explicitly picks a chat from the rail to resume).
  const onNewChat = (): void => {
    stopPolling();
    setPendingQuestion(null);
    setPending(false);
    setCurrentChatId(null);
    setTurns([]);
    setDraft('');
    setErrorCopy(null);
    setScopeTitles(null);
    setAttachment(null);
    setAttachError(null);
    setCanvasExists(false);
  };

  /**
   * "Add to canvas" affordance on chat answers (issue #3 UX pass).
   *
   * Earlier version sent ONLY the user's original question and let the
   * canvas-update flow re-derive the answer from scratch — burning a
   * second retrieval pass, losing the original answer's nuance, and
   * dropping any chunkId citations not already in the canvas snapshot.
   * Quality cliff caught in prod 2026-06-09: a 40-column XML mapping
   * grounded in a CSV attachment came back as an 8-column generic stub.
   *
   * The fix: pass the ORIGINAL answer text + its citations through to
   * the canvas turn. The instruction tells the agent to integrate the
   * answer verbatim, preserving structure and chunkId markers. The
   * citations seed the canvas snapshot so the validator doesn't strip
   * them.
   */
  const addAnswerToCanvas = useCallback(
    async (
      messageId: string,
      query: string,
      answer: string,
      citations: AgentCitation[],
    ): Promise<void> => {
      if (!currentChatId) return;
      setAddingToCanvas((prev) => new Set(prev).add(messageId));
      try {
        // Short instruction stays under the 8000-char query cap; the
        // answer itself goes in priorAnswerText (80k cap). The agent
        // formats the integration block from priorAnswerText with the
        // canonical "Answer to integrate (verbatim):" marker the
        // canvas_system prompt + classifier both recognise.
        //
        // Two modes:
        //  - canvas EXISTS for this chat → update: agent splices the
        //    answer into the existing canvas (replace overlapping
        //    section, otherwise insert at natural location).
        //  - canvas does NOT exist yet → create: agent bootstraps a new
        //    canvas seeded with this answer. The starter template comes
        //    from `canvasNextStarter` (the user's pick on the input-bar
        //    Canvas button); defaults to 'blank' when they didn't pick
        //    one. canvasNextMode/Starter clear after the create lands —
        //    the dispatch is single-shot.
        const isCreate = !canvasExists;
        const instruction = isCreate
          ? 'Create the canvas seeded with the attached answer. Preserve its structure, code blocks (whitespace exact), tables, and every inline [chunkId: ...] citation. Already grounded — do not retrieve new sources. Use the starter template only as a structural suggestion; the answer is the primary content.'
          : 'Integrate the attached answer into the canvas. Preserve its structure, code blocks (whitespace exact), tables, and every inline [chunkId: ...] citation. Already grounded — do not retrieve new sources. If a section in the canvas already covers the same topic, replace it; otherwise add as a new section at the most natural location.';
        const payload: {
          query: string;
          chatId: string;
          canvasMode: 'create' | 'update';
          starterTemplate?: CanvasStarterTemplate;
          priorAnswerText: string;
          priorAnswerQuestion: string;
          priorAnswerCitations: AgentCitation[];
        } = {
          query: instruction,
          chatId: currentChatId,
          canvasMode: isCreate ? 'create' : 'update',
          priorAnswerText: answer,
          priorAnswerQuestion: query,
          priorAnswerCitations: citations,
        };
        if (isCreate) {
          payload.starterTemplate = canvasNextStarter ?? 'blank';
        }
        await apiPost('/api/v1/agent/ask', payload);
        setCanvasRefreshKey((k) => k + 1);
        if (isCreate) {
          // Single-shot consumed — clear so a regular ask after this
          // doesn't unintentionally try to create a second canvas.
          setCanvasNextMode(null);
          setCanvasNextStarter(null);
        }
      } catch (err) {
        const msg = err instanceof Error ? err.message : 'Could not update canvas.';
        setErrorCopy(msg);
      } finally {
        setAddingToCanvas((prev) => {
          const next = new Set(prev);
          next.delete(messageId);
          return next;
        });
      }
    },
    [currentChatId, canvasExists, canvasNextStarter],
  );

  /**
   * Hydrate the page from a persisted chat. Shared by the rail-click path
   * (`onPickChat`) and the on-mount nav-away recovery path.
   *
   * If the last persisted message is a user message without an assistant
   * follow-up, that question is set as the pending question and a poll
   * loop watches for the assistant reply that the backend is still writing.
   */
  const hydrateFromChat = async (
    chatId: string,
    opts: { resumePolling: boolean },
  ): Promise<void> => {
    setErrorCopy(null);
    const thread = await apiGet<ChatThreadResponse>(`/api/v1/chats/${chatId}`);
    setCurrentChatId(thread.chat.chatId);
    setDraft('');
    const pairs: ChatState[] = [];
    let i = 0;
    while (i < thread.messages.length) {
      const userMsg = thread.messages[i];
      const assistantMsg = thread.messages[i + 1];
      if (userMsg?.role !== 'user') break;
      if (assistantMsg?.role === 'assistant') {
        pairs.push({
          question: userMsg.content,
          answer: {
            requestId: assistantMsg.messageId,
            chatId: thread.chat.chatId,
            answer: assistantMsg.content,
            citations: assistantMsg.sources,
            outcome: 'success',
          },
          scopeTitles: null,
          messageId: assistantMsg.messageId,
          ...(assistantMsg.modelId ? { modelId: assistantMsg.modelId } : {}),
          ...(assistantMsg.feedback ? { initialFeedback: assistantMsg.feedback } : {}),
          ...(assistantMsg.sourceFlags ? { initialSourceFlags: assistantMsg.sourceFlags } : {}),
          ...(userMsg.attachment
            ? {
                attachment: {
                  filename: userMsg.attachment.filename,
                  sizeBytes: userMsg.attachment.sizeBytes,
                  userMessageId: userMsg.messageId,
                },
              }
            : {}),
        });
        i += 2;
      } else {
        // Trailing user message with no assistant reply. Two cases:
        //   1. Recent (< 3 min old): agent is probably still working
        //      server-side, or the previous tab navigated away mid-call.
        //      Surface as pending and poll for the answer.
        //   2. Stale (≥ 3 min old): a prior request crashed (422, 500,
        //      Cloud Run timeout, etc.) and never wrote an assistant
        //      message. Polling will never find one — show a clear
        //      "previous request failed" affordance instead of an
        //      infinite typing indicator.
        const orphanAgeMs = Date.now() - new Date(userMsg.createdAt).getTime();
        const STALE_THRESHOLD_MS = 3 * 60 * 1000;
        if (opts.resumePolling && orphanAgeMs < STALE_THRESHOLD_MS) {
          setPendingQuestion(userMsg.content);
          setPending(true);
          // Surface Cancel during hydration polling too — without it the
          // user has no way out if the in-flight request never lands.
          setShowAbort(true);
          schedulePoll(thread.chat.chatId, pairs.length);
        } else if (opts.resumePolling) {
          // Stale orphan — surface a recoverable error so the user can
          // re-ask. Restore the question into the draft so they can
          // tweak + retry with one click.
          setDraft(userMsg.content);
          setErrorCopy(
            "A previous answer didn't complete. Your question is restored below — try sending it again.",
          );
        }
        break;
      }
    }
    setTurns(pairs);
  };

  const onPickChat = async (chatId: string): Promise<void> => {
    stopPolling();
    setPending(false);
    setPendingQuestion(null);
    try {
      await hydrateFromChat(chatId, { resumePolling: true });
    } catch (err) {
      if (err instanceof ApiError && err.status === 404) {
        setErrorCopy('This conversation no longer exists.');
      } else {
        setErrorCopy(FALLBACK_ERROR);
      }
    }
  };

  function stopPolling(): void {
    if (pollTimerRef.current) {
      clearTimeout(pollTimerRef.current);
      pollTimerRef.current = null;
    }
  }

  /**
   * Poll chat-history every 3s for an assistant message at index
   * `expectedPairIndex * 2 + 1`. Stop when found, on error, or after
   * ~5 minutes. Only one poll loop runs at a time (stopPolling clears
   * any prior timer).
   */
  function schedulePoll(chatId: string, expectedPairIndex: number, attempt = 0): void {
    stopPolling();
    if (attempt > 100) {
      // ~5 min budget. Surface a soft error; user can refresh to retry.
      setPending(false);
      setPendingQuestion(null);
      setErrorCopy('The response took too long. Please refresh the page to try again.');
      return;
    }
    pollTimerRef.current = setTimeout(() => {
      // Backgrounded tabs were a major contributor to the 2026-05-18
      // rate-limit storm — defer this tick (without consuming an attempt)
      // until the user comes back. The next visibilitychange or the next
      // 3s timer firing will pick it up.
      if (typeof document !== 'undefined' && document.hidden) {
        schedulePoll(chatId, expectedPairIndex, attempt);
        return;
      }
      void (async () => {
        try {
          const thread = await apiGet<ChatThreadResponse>(`/api/v1/chats/${chatId}`);
          const targetIdx = expectedPairIndex * 2 + 1;
          const assistant = thread.messages[targetIdx];
          if (assistant?.role === 'assistant') {
            const userMsg = thread.messages[targetIdx - 1];
            if (userMsg?.role === 'user') {
              setTurns((prev) => [
                ...prev,
                {
                  question: userMsg.content,
                  answer: {
                    requestId: assistant.messageId,
                    chatId,
                    answer: assistant.content,
                    citations: assistant.sources,
                    outcome: 'success',
                  },
                  scopeTitles: null,
                  messageId: assistant.messageId,
                  ...(assistant.modelId ? { modelId: assistant.modelId } : {}),
                  ...(assistant.feedback ? { initialFeedback: assistant.feedback } : {}),
                  ...(assistant.sourceFlags ? { initialSourceFlags: assistant.sourceFlags } : {}),
                  ...(userMsg.attachment
                    ? {
                        attachment: {
                          filename: userMsg.attachment.filename,
                          sizeBytes: userMsg.attachment.sizeBytes,
                          userMessageId: userMsg.messageId,
                        },
                      }
                    : {}),
                },
              ]);
            }
            setPending(false);
            setPendingQuestion(null);
            setHistoryRefreshKey((k) => k + 1);
            return;
          }
          schedulePoll(chatId, expectedPairIndex, attempt + 1);
        } catch {
          // Transient — keep polling.
          schedulePoll(chatId, expectedPairIndex, attempt + 1);
        }
      })();
    }, 3000);
  }

  // On mount: if sessionStorage has an active chatId, restore the chat.
  // Resumes any in-flight poll for a trailing user-message without an
  // assistant follow-up. Empty deps — runs once per mount.
  useEffect(() => {
    const stored = sessionStorage.getItem(CHAT_ID_STORAGE_KEY);
    if (!stored) return;
    mountHydrationsRef.current += 1;
    void hydrateFromChat(stored, { resumePolling: true })
      .catch((err) => {
        if (err instanceof ApiError && err.status === 404) {
          // Stale storage — chat was deleted on another tab.
          sessionStorage.removeItem(CHAT_ID_STORAGE_KEY);
        }
      })
      .finally(() => {
        mountHydrationsRef.current -= 1;
        applyPendingPrefill();
      });
    return () => stopPolling();
  }, []);

  // `/agent?q=<question>` deep link (Documents empty state, video library).
  // Prefill + focus the ask box, never auto-submit, then drop `q` from the
  // URL (replace — no history entry) so reload/Back doesn't prefill again.
  // Other params are left alone.
  const qParam = searchParams.get('q');
  useEffect(() => {
    if (qParam === null) return;
    pendingPrefillRef.current = readQuestionParam(searchParams);
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete('q');
        return next;
      },
      { replace: true },
    );
    applyPendingPrefill();
  }, [qParam]);

  useEffect(() => {
    if (focusDraftToken === 0) return;
    const el = draftInputRef.current;
    // Disabled while a resumed in-flight answer is still streaming in; the
    // prefilled draft waits there until it lands.
    if (!el || el.disabled) return;
    el.focus();
    el.setSelectionRange(el.value.length, el.value.length);
  }, [focusDraftToken]);

  // Per-turn inline-source-list expansion. Citations are collapsed by
  // default; clicking the "N sources" pill or an inline [N] superscript
  // expands the list for that turn. Keyed by turn index (stable within a
  // session — turns only append). Session-scoped state only — we don't
  // persist across reloads.
  const [expandedTurns, setExpandedTurns] = useState<Set<number>>(() => new Set());
  // Per-(turn, chunkId) ref registry so inline [N] clicks can scroll the
  // matching card into view and trigger a brief ring highlight.
  const citationCardRefs = useRef<Map<string, HTMLLIElement>>(new Map());
  const [highlightedCite, setHighlightedCite] = useState<string | null>(null);
  const highlightTimer = useRef<number | null>(null);

  const handleCiteClick = (turnIndex: number, chunkId: string): void => {
    setExpandedTurns((prev) => {
      if (prev.has(turnIndex)) return prev;
      const next = new Set(prev);
      next.add(turnIndex);
      return next;
    });
    // Defer the scroll one tick so the just-expanded list is in the DOM.
    window.requestAnimationFrame(() => {
      const key = `${turnIndex}:${chunkId}`;
      const node = citationCardRefs.current.get(key);
      if (node) {
        node.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }
      setHighlightedCite(key);
      if (highlightTimer.current !== null) {
        window.clearTimeout(highlightTimer.current);
      }
      highlightTimer.current = window.setTimeout(() => {
        setHighlightedCite(null);
        highlightTimer.current = null;
      }, 1500);
    });
  };

  const toggleTurnExpanded = (turnIndex: number): void => {
    setExpandedTurns((prev) => {
      const next = new Set(prev);
      if (next.has(turnIndex)) next.delete(turnIndex);
      else next.add(turnIndex);
      return next;
    });
  };

  const registerCitationRef = (
    turnIndex: number,
    chunkId: string,
    node: HTMLLIElement | null,
  ): void => {
    const key = `${turnIndex}:${chunkId}`;
    if (node) citationCardRefs.current.set(key, node);
    else citationCardRefs.current.delete(key);
  };

  // Canvas resize handle — mouseDown captures starting state, document-level
  // mousemove/mouseup track the drag. Width is clamped to keep the chat at
  // least 360px wide.
  useEffect(() => {
    if (!draggingCanvas) return;
    const onMove = (e: MouseEvent): void => {
      const vw = window.innerWidth;
      const next = Math.max(320, Math.min(vw - 360, vw - e.clientX));
      setCanvasWidth(next);
    };
    const onUp = (): void => setDraggingCanvas(false);
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
    return () => {
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
    };
  }, [draggingCanvas, setCanvasWidth]);

  return (
    <Layout>
      <div className="flex h-full">
        <ChatHistoryRail
          currentChatId={currentChatId}
          onPickChat={(id) => {
            void onPickChat(id);
            setHistoryOpen(false);
          }}
          onNewChat={() => {
            onNewChat();
            setHistoryOpen(false);
          }}
          refreshKey={historyRefreshKey}
          open={historyOpen}
          onClose={() => setHistoryOpen(false)}
          desktopCollapsed={historyCollapsed}
        />
        <div className="min-w-0 flex-1 overflow-y-auto">
          <div className="mx-auto w-full max-w-3xl px-4 py-6 sm:px-6 sm:py-8 lg:px-10 lg:py-10">
            <header className="mb-6">
              {/* Mobile-only: open chat history. Hidden on md+ where the rail is always visible. */}
              <button
                type="button"
                onClick={() => setHistoryOpen(true)}
                className="mb-3 inline-flex items-center gap-1.5 rounded-md border border-brand-200 px-2.5 py-1.5 text-xs font-medium text-ink-800 hover:bg-brand-50 md:hidden"
              >
                <HistoryIcon size={14} /> Previous conversations
              </button>
              {/* Desktop-only: toggle the chat-history rail collapse. Lives
                  at the top of the chat content so it stays reachable even
                  when the rail is hidden. Issue #3 UX pass. */}
              <button
                type="button"
                onClick={() => setHistoryCollapsed((v) => !v)}
                aria-label={historyCollapsed ? 'Show conversations' : 'Hide conversations'}
                title={historyCollapsed ? 'Show conversations' : 'Hide conversations'}
                className="mb-3 hidden md:inline-flex items-center gap-1.5 rounded-md border border-nebula-border-soft px-2 py-1.5 text-xs text-nebula-fg-3 hover:bg-nebula-tint hover:text-nebula-fg-1"
              >
                <HistoryIcon size={14} />
                {historyCollapsed ? 'Show conversations' : 'Hide conversations'}
              </button>
              <h1 className="text-2xl font-display font-semibold text-ink-900">Knowledge Agent</h1>
              <p className="mt-1 text-sm text-ink-900/60">
                Ask a question about OneNebula — policies, processes, products, source code,
                Confluence pages, and training videos. Answers come only from verified internal
                sources.
              </p>
            </header>

            {turns.length === 0 && !pending && !errorCopy && <EmptyState />}

            <div className="space-y-6">
              {turns.map((turn, idx) => (
                <ChatTurn
                  key={idx}
                  turnIndex={idx}
                  turn={turn}
                  isLast={idx === turns.length - 1}
                  onChangeScope={() => setScopePickerOpen(true)}
                  chatId={currentChatId}
                  sourcesExpanded={expandedTurns.has(idx)}
                  onToggleSources={() => toggleTurnExpanded(idx)}
                  onCiteClick={(chunkId) => handleCiteClick(idx, chunkId)}
                  registerCitationRef={(chunkId, node) => registerCitationRef(idx, chunkId, node)}
                  highlightedCite={highlightedCite}
                  canvasEnabled={flags.canvasEnabled}
                  canvasAvailable={canvasExists}
                  onAddToCanvas={addAnswerToCanvas}
                  addingToCanvasSet={addingToCanvas}
                />
              ))}
            </div>

            {pending && (
              <div className="mt-6 space-y-3">
                {pendingQuestion && (
                  <div className="rounded-2xl bg-brand-50/70 px-5 py-3 text-sm text-ink-900">
                    <p className="text-[11px] font-semibold uppercase tracking-wider text-ink-900/40">
                      Your question
                    </p>
                    <p className="mt-1 whitespace-pre-wrap">{pendingQuestion}</p>
                  </div>
                )}
                <div className="rounded-2xl border border-ring2 bg-white px-5 py-4 shadow-card">
                  {streamingText ? (
                    // Slice 7 — streaming render. Show the partial answer
                    // as it arrives. We strip [chunkId: …] markers
                    // visually (citations attach on the final response)
                    // so users don't see hash strings while reading.
                    <div className="space-y-2">
                      <p className="whitespace-pre-wrap text-sm leading-relaxed text-ink-900">
                        {streamingText.replace(/\[chunkId:[^\]]*\]/g, '')}
                      </p>
                      <div className="flex items-center gap-2 text-[11px] text-ink-900/45">
                        <TypingDots />
                        <span>Response on the way…</span>
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-center gap-3">
                      <TypingDots />
                      <span className="text-sm text-ink-900/70">Knowledge Agent is thinking…</span>
                    </div>
                  )}
                </div>
              </div>
            )}

            {errorCopy && (
              <div className="mt-6 flex items-start gap-3 rounded-2xl border border-amber-300/70 bg-amber-50 px-5 py-4 text-sm text-amber-900">
                <AlertTriangleIcon size={18} className="mt-0.5 shrink-0" />
                <p>{errorCopy}</p>
              </div>
            )}

            <div ref={bottomRef} />

            {/* Multi-turn context banner — surfaces ONLY when the thread has
                grown past the 10-message sliding window (= 5 Q&A pairs).
                Tells Andre the agent now keeps only the most recent 5 Q&As
                verbatim plus a sticky head of the original question.
                Hidden on shorter threads to avoid visual clutter. */}
            {turns.length >= 5 && (
              <div className="mt-4 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
                From now on the assistant only keeps your last 5 questions and answers in context.
                Earlier messages remain in your history but no longer count fully toward follow-up
                questions — your original question still travels along.
              </div>
            )}

            {/* Compound chat input — filter chips, textarea, toolbar in a
                single bordered card. Replaces the previous segmented mode
                control + bare textarea + standalone scope chip. */}
            <CompoundChatInput
              inputRef={draftInputRef}
              draft={draft}
              setDraft={setDraft}
              pending={pending}
              onSubmit={() => void submit()}
              onCancel={cancel}
              answerMode={answerMode}
              setAnswerMode={setAnswerMode}
              citationDepth={citationDepth}
              setCitationDepth={setCitationDepth}
              modelId={modelId}
              setModelId={setModelId}
              showModelPicker={true}
              allowAdminModels={isAdmin}
              scopeTitles={scopeTitles}
              onOpenScopePicker={() => setScopePickerOpen(true)}
              totalDocsInScope={
                scopeTitles && scopeTitles.length > 0
                  ? scopeTitles.length
                  : allDocs
                    ? allDocs.length
                    : null
              }
              hasTurns={turns.length > 0}
              attachment={attachment}
              setAttachment={setAttachment}
              attachError={attachError}
              setAttachError={setAttachError}
              canvasEnabled={flags.canvasEnabled}
              canvasAvailable={canvasExists}
              canvasNextMode={canvasNextMode}
              setCanvasNextMode={setCanvasNextMode}
              canvasNextStarter={canvasNextStarter}
              setCanvasNextStarter={setCanvasNextStarter}
            />
            {attachError && <p className="mt-2 text-sm text-nebula-error">{attachError}</p>}
            {answerMode !== 'quick' && (
              <p className="ov-meta mt-2 text-nebula-fg-3">Deep mode — answers take 15-30s.</p>
            )}

            {scopePickerOpen && (
              <ScopePickerModal
                currentSelection={scopeTitles}
                onApply={(next) => {
                  setScopeTitles(next);
                  setScopePickerOpen(false);
                }}
                onClose={() => setScopePickerOpen(false)}
              />
            )}

            <p className="mt-3 text-[11px] text-ink-900/40">
              The Knowledge Agent answers from internal documents only. For matters that require
              specific personal data or live system data, contact the responsible team directly.
            </p>
          </div>
        </div>
        {/* Canvas (issue #3) — mounts only when the server has enabled the
            feature AND the active chat has a canvas. CanvasPane returns
            null when no canvas exists, so the wrapper is invisible until
            the first canvas turn lands. The draggable handle between
            chat and pane lets the user retune the split per their work. */}
        {flags.canvasEnabled && currentChatId && (
          <>
            {/* Resize handle — only rendered once we know there's a
                canvas to resize against (otherwise it would be a floating
                divider with empty space on the right). */}
            {canvasExists && (
              <div
                role="separator"
                aria-orientation="vertical"
                aria-label="Resize canvas"
                onMouseDown={(e) => {
                  e.preventDefault();
                  setDraggingCanvas(true);
                }}
                className={`hidden md:block w-1 shrink-0 cursor-col-resize bg-nebula-border-soft hover:bg-nebula-primary ${
                  draggingCanvas ? 'bg-nebula-primary' : ''
                }`}
              />
            )}
            <CanvasPane
              chatId={currentChatId}
              width={canvasWidth}
              refreshKey={canvasRefreshKey}
              onCanvasStateChange={(c) => setCanvasExists(c !== null)}
            />
          </>
        )}
      </div>
    </Layout>
  );
}

function EmptyState(): JSX.Element {
  return (
    <div className="rounded-2xl border border-ring2 bg-white px-6 py-10 text-center text-sm text-ink-900/55 shadow-card">
      Ask your first question below. Answers appear with source citations.
    </div>
  );
}

function ChatTurn({
  turn,
  turnIndex,
  isLast,
  onChangeScope,
  chatId,
  sourcesExpanded,
  onToggleSources,
  onCiteClick,
  registerCitationRef,
  highlightedCite,
  canvasEnabled,
  canvasAvailable,
  onAddToCanvas,
  addingToCanvasSet,
}: {
  turn: ChatState;
  turnIndex: number;
  isLast: boolean;
  onChangeScope: () => void;
  chatId: string | null;
  sourcesExpanded: boolean;
  onToggleSources: () => void;
  onCiteClick: (chunkId: string) => void;
  registerCitationRef: (chunkId: string, node: HTMLLIElement | null) => void;
  highlightedCite: string | null;
  /** Whether the canvas feature is on. Drives button visibility on the
   *  answer footer regardless of whether a canvas exists yet. */
  canvasEnabled: boolean;
  /** Whether the active chat already has a canvas. Drives the button
   *  label: "Add to canvas" (update) vs "Create canvas from this"
   *  (create). Threaded from parent. */
  canvasAvailable: boolean;
  /** Trigger a canvas-update or canvas-create turn with the answer's
   *  question + the answer text + its citations. The answer is the
   *  primary input the agent integrates; the citations seed the
   *  validator snapshot so the answer's `[chunkId: ...]` markers don't
   *  get stripped. */
  onAddToCanvas: (
    messageId: string,
    query: string,
    answer: string,
    citations: AgentCitation[],
  ) => Promise<void>;
  /** Per-message loading guard so the button can show "Updating…". */
  addingToCanvasSet: Set<string>;
}): JSX.Element {
  const { question, answer, scopeTitles } = turn;
  const isEmptyRetrieval = answer.outcome === 'empty_retrieval';
  const scopeLabel =
    scopeTitles && scopeTitles.length > 0
      ? `${scopeTitles.length} document${scopeTitles.length === 1 ? '' : 's'}`
      : 'all documents';
  // Show which model answered whenever a non-default model was recorded — the
  // picker is open to all users now, so all should see the badge.
  const turnModelLabel = modelBadgeLabel(turn.modelId);
  return (
    <div className="space-y-3">
      {/* Question bubble — slim, brand-tinted, distinctly the user's voice */}
      <div className="rounded-2xl bg-brand-50/80 px-5 py-3 text-sm text-ink-900">
        <p className="whitespace-pre-wrap">{question}</p>
        {turn.attachment && <AttachmentChip chatId={chatId} attachment={turn.attachment} />}
      </div>

      {/* Single answer container holding prose + sources + footer. */}
      <div className="rounded-2xl border bg-white shadow-card border-ring2">
        <div className="flex items-center gap-1.5 border-b border-ring2/70 px-5 py-2 text-[11px] text-ink-900/55">
          <BookTextIcon size={11} />
          <span>Scope: {scopeLabel}</span>
          {scopeTitles && scopeTitles.length > 0 && scopeTitles.length <= 3 && (
            <span className="text-ink-900/40 truncate">({scopeTitles.join(', ')})</span>
          )}
          {/* kb_admin-only per-message model badge. Lets admins comparing
              models see which provider produced which answer when scrolling
              through a long chat. Non-admins never see this chip. */}
          {turnModelLabel && (
            <span
              className="ml-auto inline-flex items-center rounded-full bg-nebula-tint px-2 py-0.5 font-mono text-[10px] font-medium text-nebula-primary"
              title={`Answered by ${turn.modelId}`}
            >
              {turnModelLabel}
            </span>
          )}
        </div>

        <div className="px-5 py-5">
          <div className="prose prose-sm max-w-none text-sm leading-relaxed text-ink-900">
            <AnswerMarkdown
              text={answer.answer}
              citations={answer.citations}
              onCiteClick={onCiteClick}
            />
          </div>
          {isEmptyRetrieval && (
            <p className="mt-3 text-xs text-ink-900/50">{EMPTY_RETRIEVAL_HINT}</p>
          )}
        </div>

        {answer.citations.length > 0 && (
          <div className="border-t border-ring2/70 px-5 pt-3 pb-3">
            {/* Collapsed-by-default sources pill. Click expands the per-turn
                citation list; clicking an inline [N] superscript in the
                answer prose does the same and scrolls to the matching card.
                The per-turn 👍 / 👎 feedback widget sits on the same row. */}
            <div className="flex items-center gap-2 flex-wrap">
              <button
                type="button"
                onClick={onToggleSources}
                aria-expanded={sourcesExpanded}
                className="inline-flex items-center gap-1.5 rounded-full bg-nebula-tint px-3 py-1 text-[11px] font-medium text-nebula-secondary hover:bg-nebula-tint/80"
              >
                <BookTextIcon size={12} />
                {answer.citations.length} source{answer.citations.length === 1 ? '' : 's'}
                {sourcesExpanded ? <ChevronUpIcon size={12} /> : <ChevronDownIcon size={12} />}
              </button>
            </div>
            {sourcesExpanded && (
              <ul className="mt-3 divide-y divide-ring2/50">
                {answer.citations.map((c, idx) => (
                  <CitationRow
                    key={c.chunkId}
                    citation={c}
                    index={idx + 1}
                    refCallback={(node) => registerCitationRef(c.chunkId, node)}
                    highlighted={highlightedCite === `${turnIndex}:${c.chunkId}`}
                    chatId={chatId}
                    messageId={turn.messageId ?? null}
                    initialFlag={turn.initialSourceFlags?.[c.chunkId] ?? null}
                  />
                ))}
              </ul>
            )}
          </div>
        )}

        <FeedbackFooter
          answer={answer.answer}
          chatId={chatId}
          messageId={turn.messageId ?? null}
          initialFeedback={turn.initialFeedback ?? null}
          query={question}
          citations={answer.citations}
          canvasEnabled={canvasEnabled}
          canvasAvailable={canvasAvailable}
          onAddToCanvas={onAddToCanvas}
          addingToCanvas={turn.messageId ? addingToCanvasSet.has(turn.messageId) : false}
        />
      </div>

      {/* After-response scope hint on the most recent turn */}
      {isLast && (
        <div className="flex justify-end">
          <button
            type="button"
            onClick={onChangeScope}
            className="text-[11px] text-ink-900/55 hover:text-brand-800 hover:underline"
          >
            Not what you were looking for? Restrict the scope to specific documents →
          </button>
        </div>
      )}
    </div>
  );
}

function FeedbackFooter({
  answer,
  chatId,
  messageId,
  initialFeedback,
  query,
  citations,
  canvasEnabled,
  canvasAvailable,
  onAddToCanvas,
  addingToCanvas,
}: {
  answer: string;
  chatId: string | null;
  messageId: string | null;
  initialFeedback: ChatMessageFeedback | null;
  query: string;
  citations: AgentCitation[];
  /** Whether the canvas feature is on for this caller. Drives button
   *  visibility regardless of whether a canvas already exists — the
   *  button covers both "create from this answer" and "update existing
   *  canvas". */
  canvasEnabled: boolean;
  /** Whether a canvas already exists for the active chat. Drives the
   *  button label: present → "Add to canvas" (update flow); absent →
   *  "Create canvas from this answer" (bootstrap flow). */
  canvasAvailable: boolean;
  /** Trigger the canvas-update or canvas-create flow with this turn's
   *  question + answer text + citations. The parent integrates the
   *  answer verbatim; passing citations seeds the validator snapshot so
   *  the answer's chunkIds don't get stripped. */
  onAddToCanvas: (
    messageId: string,
    query: string,
    answer: string,
    citations: AgentCitation[],
  ) => Promise<void>;
  /** Loading state — true while the canvas-update agent call is in flight. */
  addingToCanvas: boolean;
}): JSX.Element {
  const [feedback, setFeedback] = useState<'up' | 'down' | null>(
    initialFeedback ? (initialFeedback.verdict === 'positive' ? 'up' : 'down') : null,
  );
  // Inline "what went wrong" panel that opens beneath the footer when the
  // user clicks the downthumb. Replaces the auto-opening ChangeRequestModal
  // — the modal is still available behind a sub-link for users who want
  // the heavier email flow.
  const [downPanelOpen, setDownPanelOpen] = useState(false);
  const [commentDraft, setCommentDraft] = useState('');
  const [commentSubmitting, setCommentSubmitting] = useState(false);
  const [commentDone, setCommentDone] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [commentError, setCommentError] = useState<string | null>(null);

  // Fire-and-forget POST to /api/v1/agent/feedback. We optimistically flip
  // the local state on click; if the server rejects we revert. The binary
  // verdict is captured FIRST (no comment) so a slow connection doesn't
  // gate the dataset on the user typing. A follow-up POST with the same
  // verdict + comment overwrites the prior record (the backend's
  // submitFeedback is idempotent and pushes the prior state onto
  // feedbackHistory[]).
  const submit = useCallback(
    async (
      verdict: 'positive' | 'negative',
      opts: { comment?: string } = {},
    ): Promise<{ ok: boolean; error?: string }> => {
      if (!chatId) {
        return { ok: false, error: 'Chat not saved yet — wait a moment and retry.' };
      }
      if (!messageId) {
        return {
          ok: false,
          error: 'This answer is still being saved. Try again in a few seconds.',
        };
      }
      setSubmitting(true);
      try {
        await apiPost(`/api/v1/agent/feedback`, {
          chatId,
          messageId,
          verdict,
          ...(opts.comment ? { comment: opts.comment } : {}),
        });
        return { ok: true };
      } catch (err) {
        if (!opts.comment) setFeedback(null);
        const msg = err instanceof Error ? err.message : 'Could not save feedback.';
        return { ok: false, error: msg };
      } finally {
        setSubmitting(false);
      }
    },
    [chatId, messageId],
  );

  // 500-char soft counter, 2000-char hard cap.
  const COMMENT_SOFT = 500;
  const COMMENT_HARD = 2000;
  const commentLen = commentDraft.length;
  const overSoft = commentLen > COMMENT_SOFT;

  const submitComment = async (): Promise<void> => {
    const trimmed = commentDraft.trim();
    if (!trimmed || commentSubmitting) return;
    setCommentSubmitting(true);
    setCommentError(null);
    const result = await submit('negative', { comment: trimmed });
    setCommentSubmitting(false);
    if (result.ok) {
      setCommentDone(true);
      // Collapse after a brief acknowledgement.
      setTimeout(() => setDownPanelOpen(false), 800);
    } else {
      setCommentError(result.error ?? 'Could not save feedback. Please try again.');
    }
  };

  return (
    <div className="border-t border-ring2/70">
      <div className="flex items-center gap-1 px-3 py-2">
        <IconButton
          active={feedback === 'up'}
          onClick={() => {
            if (submitting) return;
            setFeedback('up');
            // Closing the down-panel if the user flips their mind.
            setDownPanelOpen(false);
            void submit('positive');
          }}
          title="Helpful"
          aria-label="Helpful"
        >
          <ThumbsUpIcon size={14} />
        </IconButton>
        {/* Down-vote captures the binary verdict immediately, then opens an
            inline panel asking for a comment. The legacy
            ChangeRequestModal email flow is reachable via a sub-link in
            the panel for users who want to write a longer report. */}
        <IconButton
          active={feedback === 'down'}
          onClick={() => {
            if (submitting) return;
            setFeedback('down');
            setDownPanelOpen(true);
            setCommentDone(false);
            void submit('negative');
          }}
          title="Not helpful"
          aria-label="Not helpful"
        >
          <ThumbsDownIcon size={14} />
        </IconButton>
        <CopyIconButton text={answer} />
        <DownloadMenu question={query} answer={answer} citations={citations} />
        {/* Add-to-canvas (issue #3) — shown whenever the feature is on.
            Click handler in the parent picks update vs create based on
            whether a canvas already exists for the chat. Button label
            adapts so the user knows which action they're triggering.
            Hidden on the "Created/Updated the canvas" status lines. */}
        {canvasEnabled && messageId && !isCanvasTranscriptMarker(answer) && (
          <button
            type="button"
            disabled={addingToCanvas}
            onClick={() => void onAddToCanvas(messageId, query, answer, citations)}
            title={
              canvasAvailable
                ? 'Update the existing canvas with this answer'
                : 'Create a new canvas seeded with this answer'
            }
            aria-label={
              canvasAvailable ? 'Add this answer to the canvas' : 'Create canvas from this answer'
            }
            className="ml-1 inline-flex items-center gap-1 rounded-md border border-nebula-border-soft px-2 py-1 text-[11px] font-medium text-nebula-secondary hover:bg-nebula-tint disabled:opacity-50"
          >
            {addingToCanvas
              ? canvasAvailable
                ? 'Updating canvas…'
                : 'Creating canvas…'
              : canvasAvailable
                ? 'Add to canvas'
                : 'Create canvas from this'}
          </button>
        )}
      </div>

      {downPanelOpen && (
        <div className="border-t border-ring2/70 bg-nebula-tint/40 px-4 py-3">
          {commentDone ? (
            <p className="text-xs text-ink-900/70">Thank you — your feedback was recorded.</p>
          ) : (
            <>
              <label
                htmlFor={`fb-comment-${messageId ?? 'pending'}`}
                className="block text-xs font-medium text-ink-900"
              >
                What was wrong with this answer?
              </label>
              <p className="mt-0.5 text-[11px] text-ink-900/60">
                Help us improve by telling us what was missing, inaccurate, or unclear.
              </p>
              <textarea
                id={`fb-comment-${messageId ?? 'pending'}`}
                rows={4}
                value={commentDraft}
                onChange={(e) => {
                  const next = e.target.value.slice(0, COMMENT_HARD);
                  setCommentDraft(next);
                }}
                className="mt-2 block w-full resize-y rounded-md border border-ring2 bg-white px-3 py-2 text-sm text-ink-900 outline-none focus:border-nebula-secondary"
                placeholder="Tell us what went wrong…"
              />
              <div className="mt-1 text-[11px]">
                <span className={overSoft ? 'text-amber-700' : 'text-ink-900/45'}>
                  {commentLen} / {COMMENT_SOFT}
                  {overSoft ? ` (hard cap ${COMMENT_HARD})` : ''}
                </span>
              </div>
              <div className="mt-2 flex items-center gap-2">
                <button
                  type="button"
                  onClick={submitComment}
                  disabled={commentSubmitting || commentDraft.trim().length === 0}
                  className="inline-flex items-center rounded-md bg-nebula-secondary px-3 py-1.5 text-xs font-medium text-white hover:bg-nebula-primary disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {commentSubmitting ? 'Submitting…' : 'Submit'}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setDownPanelOpen(false);
                    setCommentDraft('');
                  }}
                  className="inline-flex items-center rounded-md border border-ring2 bg-white px-3 py-1.5 text-xs font-medium text-ink-900 hover:bg-ink-900/5"
                >
                  Skip
                </button>
              </div>
              {commentError && (
                <p className="mt-2 text-xs text-red-700" role="alert">
                  {commentError}
                </p>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * Compact paperclip chip rendered inside the user-question bubble when the
 * turn had an attached file. Clickable when we know the userMessageId
 * (chat-history hydration path). On the fresh optimistic turn the chip
 * still renders so the user sees their upload was carried, but without a
 * link — they already have the file locally and a click-through would
 * 404 until the chat doc lands.
 */
function AttachmentChip({
  chatId,
  attachment,
}: {
  chatId: string | null;
  attachment: { filename: string; sizeBytes: number; userMessageId?: string };
}): JSX.Element {
  const sizeKb = Math.max(1, Math.round(attachment.sizeBytes / 1024));
  const downloadable = Boolean(chatId && attachment.userMessageId);
  const href = downloadable
    ? `/api/v1/chats/${chatId}/messages/${attachment.userMessageId}/attachment`
    : undefined;
  const baseClasses =
    'mt-2 inline-flex items-center gap-1.5 rounded-md border border-ring2/70 bg-white px-2 py-1 text-[11px] text-ink-900/80 max-w-full';
  const interactiveClasses = downloadable
    ? ' hover:border-nebula-secondary hover:text-nebula-secondary'
    : ' opacity-80';
  return href ? (
    <a
      href={href}
      className={baseClasses + interactiveClasses}
      title={`Download ${attachment.filename}`}
    >
      <AttachFileIcon size={12} />
      <span className="truncate">
        {attachment.filename} <span className="text-ink-900/45">({sizeKb} KB)</span>
      </span>
    </a>
  ) : (
    <span className={baseClasses + interactiveClasses} title={attachment.filename}>
      <AttachFileIcon size={12} />
      <span className="truncate">
        {attachment.filename} <span className="text-ink-900/45">({sizeKb} KB)</span>
      </span>
    </span>
  );
}

function IconButton({
  children,
  onClick,
  active = false,
  title,
  ...rest
}: {
  children: React.ReactNode;
  onClick: () => void;
  active?: boolean;
  title?: string;
  'aria-label'?: string;
}): JSX.Element {
  return (
    <button
      type="button"
      onClick={onClick}
      title={title}
      className={`inline-flex h-7 w-7 items-center justify-center rounded-md transition-colors ${
        active
          ? 'bg-brand-50 text-brand-700'
          : 'text-ink-900/50 hover:bg-ink-900/5 hover:text-ink-900'
      }`}
      {...rest}
    >
      {children}
    </button>
  );
}

function CopyIconButton({ text }: { text: string }): JSX.Element {
  const [copied, setCopied] = useState(false);
  const handleCopy = async (): Promise<void> => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      /* clipboard blocked — silent fail */
    }
  };
  return (
    <button
      type="button"
      onClick={handleCopy}
      title={copied ? 'Copied' : 'Copy answer'}
      aria-label={copied ? 'Copied' : 'Copy answer'}
      className="inline-flex h-7 w-7 items-center justify-center rounded-md text-ink-900/50 hover:bg-ink-900/5 hover:text-ink-900"
    >
      {copied ? <CheckIcon size={14} /> : <CopyIcon size={14} />}
    </button>
  );
}

interface FullChunk {
  chunkId: string;
  sourceFile: string;
  page: number;
  chunkIndex: number;
  chunkText: string;
  tenantId: string;
  createdAt?: string;
  createdBy?: string;
  embeddingPresent?: boolean;
}

function CitationRow({
  citation,
  index,
  refCallback,
  highlighted = false,
  chatId = null,
  messageId = null,
  initialFlag = null,
}: {
  citation: AgentCitation;
  index: number;
  refCallback?: (node: HTMLLIElement | null) => void;
  highlighted?: boolean;
  chatId?: string | null;
  messageId?: string | null;
  initialFlag?: { reason: SourceFlagReason; comment?: string } | null;
}): JSX.Element {
  const { user } = useAuth();
  const isAdmin = user?.role === 'kb_admin';
  const [expanded, setExpanded] = useState(false);
  const [chunkOpen, setChunkOpen] = useState(false);
  // Per-source flag local state. Seeded from hydrated history; updated on
  // submit. The popover renders inline (not a portal) — the click target
  // anchors it visually.
  const [flagOpen, setFlagOpen] = useState(false);
  const [flagged, setFlagged] = useState<{ reason: SourceFlagReason } | null>(
    initialFlag ? { reason: initialFlag.reason } : null,
  );
  const [flagReason, setFlagReason] = useState<SourceFlagReason>(
    initialFlag?.reason ?? 'inaccurate',
  );
  const [flagComment, setFlagComment] = useState(initialFlag?.comment ?? '');
  const [flagSubmitting, setFlagSubmitting] = useState(false);
  const [flagError, setFlagError] = useState<string | null>(null);
  const FLAG_SOFT = 500;
  const FLAG_HARD = 2000;

  const submitFlag = async (): Promise<void> => {
    if (!chatId || !messageId || flagSubmitting) return;
    setFlagSubmitting(true);
    setFlagError(null);
    const trimmed = flagComment.trim();
    try {
      await apiPost(`/api/v1/agent/source-flag`, {
        chatId,
        messageId,
        chunkId: citation.chunkId,
        reason: flagReason,
        ...(trimmed ? { comment: trimmed } : {}),
      });
      setFlagged({ reason: flagReason });
      setFlagOpen(false);
    } catch {
      setFlagError('Could not submit the flag. Please try again.');
    } finally {
      setFlagSubmitting(false);
    }
  };

  // Prefer the sourceFile field when present (added once chunks were
  // re-ingested with the manifest). Older chunks without it fall back to
  // re-adding `.pdf` to the title — works for the original ingest run.
  const filename = citation.sourceFile ?? `${citation.title}.pdf`;
  // Ring 2 citations link out to the external article (NHG / Trimbos /
  // Thuisarts.nl / GGZ-standaarden / RIVM). Other rings go through
  // sourceFileHref so confluence-{pageId}-…md citations route to the
  // actual Confluence page instead of hitting the backend's
  // /api/v1/agent/sources/ endpoint (which only serves docs + images
  // and rejects markdown derivatives with "Only document and image
  // sources are downloadable"). Bug fixed 2026-05-17.
  const sourceUrl =
    citation.ring === 'ring2' && citation.sourceUrl
      ? citation.sourceUrl
      : citationHref({
          sourceType: citation.sourceType,
          videoId: citation.videoId,
          startSeconds: citation.startSeconds,
          sourceFile: filename,
        });
  const subtitle =
    citation.ring === 'ring2'
      ? citation.ring2Source && RING2_SOURCE_LABELS[citation.ring2Source]
        ? `Verified source · ${RING2_SOURCE_LABELS[citation.ring2Source]}`
        : 'Verified external source'
      : citation.ring === 'tool'
        ? 'Live agent-tool result'
        : citation.author && citation.author !== 'Unknown'
          ? citation.author
          : 'OneNebula Knowledgebase';
  const SourceIcon = pickSourceIcon(filename);
  // Aggregate tool citations have no source document behind them — render
  // the title as plain text instead of a dead /sources/ link. Search tool
  // citations DO carry a jira-*.md sourceFile and link to Atlassian.
  const linkable = citation.ring !== 'tool' || !!citation.sourceFile || !!citation.sourceUrl;

  return (
    <li
      ref={refCallback}
      className={`rounded-md py-2 transition-shadow duration-500 first:pt-1 last:pb-1 ${
        highlighted ? 'ring-2 ring-nebula-secondary ring-offset-1' : ''
      }`}
    >
      <div className="flex items-start gap-2.5">
        {/* Citation number — primary identifier matching the [N] markers in prose */}
        <span className="mt-0.5 inline-flex h-5 min-w-[1.5rem] shrink-0 items-center justify-center rounded-full bg-brand-700 px-1.5 text-[11px] font-semibold text-white">
          {index}
        </span>
        {/* Trust-tier chip. Ring 0 = OneNebula internal docs (handbook,
            policies, ways-of-working). Ring 1 = OneNebula product docs
            (OneView features, runbooks). Ring 2 (Petrol) = verified
            external sources crawled weekly. Default to Ring 1 when the
            field is missing so legacy chats (pre-slice-5) keep rendering
            correctly. */}
        {citation.ring === 'ring0' ? (
          <span
            title="OneNebula internal document — visible only to OneNebula employees."
            className="mt-0.5 inline-flex shrink-0 items-center gap-1 rounded-full border border-slate-300 bg-slate-100 px-2 py-0.5 text-[10px] font-medium text-slate-800"
          >
            <span className="h-1 w-1 rounded-full bg-slate-700" />
            OneNebula Internal
          </span>
        ) : citation.ring === 'ring2' ? (
          <span
            title="Verified external source — crawled weekly."
            className="mt-0.5 inline-flex shrink-0 items-center gap-1 rounded-full border border-ring2-border bg-ring2-bg px-2 py-0.5 text-[10px] font-medium text-ring2-fg"
          >
            <span className="h-1 w-1 rounded-full bg-ring2-fg" />
            {citation.ring2Source && RING2_SOURCE_LABELS[citation.ring2Source]
              ? RING2_SOURCE_LABELS[citation.ring2Source]
              : 'Ring 2'}
          </span>
        ) : citation.ring === 'tool' ? (
          <span
            title="Live result fetched by an agent tool — not part of the curated corpus."
            className="mt-0.5 inline-flex shrink-0 items-center gap-1 rounded-full border border-slate-300 bg-white px-2 py-0.5 text-[10px] font-medium text-slate-700"
          >
            <span className="h-1 w-1 rounded-full bg-slate-500" />
            Live · Jira
          </span>
        ) : (
          <span
            title="OneNebula curated knowledge corpus."
            className="mt-0.5 inline-flex shrink-0 items-center gap-1 rounded-full border border-ring1-border bg-ring1-bg px-2 py-0.5 text-[10px] font-medium text-ring1-fg"
          >
            <span className="h-1 w-1 rounded-full bg-ring1-fg" />
            OneNebula
          </span>
        )}
        {/* ISO 27001 — classification chip rendered next to the trust-tier
            chip when the chunk is not the implicit `internal` baseline. */}
        {citation.classification === 'confidential' && (
          <span
            title="Confidential — visible only to members of KB-Confidential."
            className="mt-0.5 inline-flex shrink-0 items-center gap-1 rounded-full border border-mosterdgeel-300 bg-mosterdgeel-50 px-2 py-0.5 text-[10px] font-medium text-mosterdgeel-700"
          >
            <span className="h-1 w-1 rounded-full bg-mosterdgeel-500" />
            Confidential
          </span>
        )}
        {citation.classification === 'restricted' && (
          <span
            title="Restricted — elevated access required."
            className="mt-0.5 inline-flex shrink-0 items-center gap-1 rounded-full border border-petrol-300 bg-petrol-50 px-2 py-0.5 text-[10px] font-medium text-petrol-700"
          >
            <span className="h-1 w-1 rounded-full bg-petrol-500" />
            Restricted
          </span>
        )}

        <div className="min-w-0 flex-1">
          <div className="flex items-baseline gap-1.5">
            {linkable ? (
              <a
                href={sourceUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 truncate text-sm font-medium text-ink-900 hover:text-brand-800 hover:underline"
                title={`Open ${filename}`}
              >
                <SourceIcon size={13} className="shrink-0 text-brand-700" />
                <span className="truncate">{citation.title}</span>
                <ExternalLinkIcon size={11} className="shrink-0 text-ink-900/40" />
              </a>
            ) : (
              <span className="inline-flex items-center gap-1 truncate text-sm font-medium text-ink-900">
                <SourceIcon size={13} className="shrink-0 text-brand-700" />
                <span className="truncate">{citation.title}</span>
              </span>
            )}
            {citation.page != null && (
              <span className="shrink-0 text-[11px] text-ink-900/45">p. {citation.page}</span>
            )}
          </div>
          <div className="truncate text-[11px] text-ink-900/50">
            {subtitle}
            {citation.documentDate && (
              <span className="text-ink-900/40">
                {' · '}
                {new Date(citation.documentDate).toLocaleDateString('en-GB', {
                  day: 'numeric',
                  month: 'short',
                  year: 'numeric',
                  timeZone: 'UTC',
                })}
              </span>
            )}
          </div>

          {expanded && (
            <div className="prose prose-sm mt-2 max-w-none rounded-lg bg-ink-900/[0.03] px-3 py-2 text-xs leading-relaxed text-ink-900/75">
              <CitationMarkdown text={citation.snippet} />
            </div>
          )}

          <div className="mt-1.5 flex items-center gap-3">
            <button
              type="button"
              onClick={() => setExpanded((v) => !v)}
              className="inline-flex items-center gap-0.5 text-[11px] text-brand-700 hover:text-brand-800"
            >
              {expanded ? (
                <>
                  <ChevronUpIcon size={11} /> Hide fragment
                </>
              ) : (
                <>
                  <ChevronDownIcon size={11} /> Show fragment
                </>
              )}
            </button>
            {isAdmin && (
              <button
                type="button"
                onClick={() => setChunkOpen(true)}
                className="font-mono text-[10px] text-ink-900/40 hover:text-brand-700 hover:underline"
                title="Open full chunk data (admin validation)"
              >
                {citation.chunkId.slice(0, 12)}…
              </button>
            )}
            {chatId && messageId && (
              <button
                type="button"
                onClick={() => setFlagOpen((v) => !v)}
                className={`inline-flex items-center gap-0.5 text-[11px] ${
                  flagged
                    ? 'text-amber-700 hover:text-amber-800'
                    : 'text-ink-900/45 hover:text-brand-700'
                }`}
                title={flagged ? 'You flagged this source' : 'Flag this source'}
                aria-label={flagged ? 'Flagged' : 'Flag this source'}
              >
                {flagged ? <FlagFilledIcon size={11} /> : <FlagIcon size={11} />}
                {flagged ? 'Flagged' : 'Flag'}
              </button>
            )}
          </div>

          {flagOpen && chatId && messageId && (
            <div className="mt-2 rounded-md border border-ring2 bg-white p-3 shadow-sm">
              <p className="text-xs font-medium text-ink-900">Why is this source a problem?</p>
              <div className="mt-2 space-y-1.5">
                {(
                  [
                    ['inaccurate', 'Inaccurate — content is wrong'],
                    ['outdated', 'Out of date — content is stale'],
                    ['wrong_document', 'Wrong document — agent cited the wrong source'],
                    ['other', 'Other'],
                  ] as Array<[SourceFlagReason, string]>
                ).map(([value, label]) => (
                  <label
                    key={value}
                    className="flex cursor-pointer items-center gap-2 text-[11px] text-ink-900"
                  >
                    <input
                      type="radio"
                      name={`flag-reason-${citation.chunkId}`}
                      value={value}
                      checked={flagReason === value}
                      onChange={() => setFlagReason(value)}
                      className="h-3 w-3 accent-nebula-secondary"
                    />
                    <span>{label}</span>
                  </label>
                ))}
              </div>
              <textarea
                rows={3}
                value={flagComment}
                onChange={(e) => setFlagComment(e.target.value.slice(0, FLAG_HARD))}
                placeholder="Optional comment…"
                className="mt-2 block w-full resize-y rounded-md border border-ring2 bg-white px-2.5 py-1.5 text-[12px] text-ink-900 outline-none focus:border-nebula-secondary"
              />
              <div className="mt-1 flex items-center justify-between text-[10px]">
                <span
                  className={flagComment.length > FLAG_SOFT ? 'text-amber-700' : 'text-ink-900/45'}
                >
                  {flagComment.length} / {FLAG_SOFT}
                  {flagComment.length > FLAG_SOFT ? ` (hard cap ${FLAG_HARD})` : ''}
                </span>
              </div>
              {flagError && <p className="mt-1 text-[11px] text-red-700">{flagError}</p>}
              <div className="mt-2 flex items-center gap-2">
                <button
                  type="button"
                  onClick={submitFlag}
                  disabled={flagSubmitting}
                  className="inline-flex items-center rounded-md bg-nebula-secondary px-2.5 py-1 text-[11px] font-medium text-white hover:bg-nebula-primary disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {flagSubmitting ? 'Submitting…' : 'Submit'}
                </button>
                <button
                  type="button"
                  onClick={() => setFlagOpen(false)}
                  className="inline-flex items-center rounded-md border border-ring2 bg-white px-2.5 py-1 text-[11px] font-medium text-ink-900 hover:bg-ink-900/5"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {chunkOpen && (
        <ChunkInspectorModal chunkId={citation.chunkId} onClose={() => setChunkOpen(false)} />
      )}
    </li>
  );
}

function chunkToMarkdown(chunkId: string, data: FullChunk): string {
  const fence = '```';
  return [
    '## Chunk validation',
    '',
    `- **chunkId:** \`${chunkId}\``,
    `- **Source file:** ${data.sourceFile}`,
    `- **Page:** ${data.page}`,
    `- **Chunk index in document:** ${data.chunkIndex}`,
    `- **Tenant:** ${data.tenantId}`,
    `- **Indexed at:** ${data.createdAt ?? '—'}`,
    `- **By:** ${data.createdBy ?? '—'}`,
    `- **Embedding present:** ${data.embeddingPresent ? 'yes (768-dim)' : 'no'}`,
    '',
    '### Full text',
    '',
    fence,
    data.chunkText,
    fence,
    '',
  ].join('\n');
}

function ChunkInspectorModal({
  chunkId,
  onClose,
}: {
  chunkId: string;
  onClose: () => void;
}): JSX.Element {
  const [data, setData] = useState<FullChunk | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    let alive = true;
    apiGet<FullChunk>(`/api/v1/agent/chunks/${chunkId}`)
      .then((d) => {
        if (alive) setData(d);
      })
      .catch((err) => {
        if (alive) setError(err instanceof ApiError ? err.message : 'Could not load chunk.');
      });
    return () => {
      alive = false;
    };
  }, [chunkId]);

  const handleCopy = async (): Promise<void> => {
    if (!data) return;
    try {
      await navigator.clipboard.writeText(chunkToMarkdown(chunkId, data));
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      /* clipboard blocked — silent fail */
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-ink-900/40 px-4"
      onClick={onClose}
    >
      <div
        className="max-h-[85vh] w-full max-w-2xl overflow-y-auto rounded-2xl bg-white p-6 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <h2 className="text-base font-semibold text-ink-900">Chunk validation</h2>
            <p className="mt-1 font-mono text-[11px] text-ink-900/55 break-all">{chunkId}</p>
          </div>
          <div className="flex shrink-0 items-center gap-1">
            <button
              type="button"
              onClick={handleCopy}
              disabled={!data}
              title={copied ? 'Copied as markdown' : 'Copy as markdown'}
              aria-label={copied ? 'Copied as markdown' : 'Copy as markdown'}
              className="inline-flex h-8 w-8 items-center justify-center rounded-md text-ink-900/55 hover:bg-ink-900/5 hover:text-ink-900 disabled:opacity-40"
            >
              {copied ? <CheckIcon size={15} /> : <CopyIcon size={15} />}
            </button>
            <button
              type="button"
              onClick={onClose}
              className="rounded-full p-1 text-ink-900/55 hover:bg-ink-900/5 hover:text-ink-900"
              aria-label="Close"
            >
              <XIcon size={18} />
            </button>
          </div>
        </div>

        {error && (
          <div className="mt-4 rounded-xl border border-amber-300/70 bg-amber-50 px-4 py-3 text-sm text-amber-900">
            {error}
          </div>
        )}
        {!data && !error && <div className="mt-4 text-sm text-ink-900/55">Loading…</div>}
        {data && (
          <dl className="mt-4 space-y-3 text-sm">
            <Field label="Source file">
              <a
                href={sourceFileHref(data.sourceFile)}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 text-brand-700 hover:underline"
              >
                {data.sourceFile} <ExternalLinkIcon size={12} />
              </a>
            </Field>
            <Field label="Page">{data.page}</Field>
            <Field label="Chunk index in document">{data.chunkIndex}</Field>
            <Field label="Tenant">{data.tenantId}</Field>
            <Field label="Indexed at">{data.createdAt ?? '—'}</Field>
            <Field label="By">{data.createdBy ?? '—'}</Field>
            <Field label="Embedding present">
              {data.embeddingPresent ? 'yes (768-dim)' : 'no'}
            </Field>
            <div>
              <dt className="text-[11px] font-semibold uppercase tracking-wide text-ink-900/50">
                Full text
              </dt>
              <dd className="prose prose-sm mt-1 max-h-96 max-w-none overflow-y-auto rounded-xl bg-ink-900/5 p-3 text-xs leading-relaxed text-ink-900/85">
                <CitationMarkdown text={data.chunkText} />
              </dd>
            </div>
          </dl>
        )}
      </div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }): JSX.Element {
  return (
    <div className="grid grid-cols-[160px_1fr] gap-3">
      <dt className="text-[11px] font-semibold uppercase tracking-wide text-ink-900/50">{label}</dt>
      <dd className="text-sm text-ink-900">{children}</dd>
    </div>
  );
}

/**
 * Compound chat input. One bordered card containing three rows:
 *   1. Scope row — document-scope dropdown ("All documents ▾") and a
 *      docs-in-scope count.
 *   2. Textarea — `draft` is the source of truth, owned by the page.
 *   3. Toolbar — Deep mode popover, Attach, Citation depth, Model, Canvas,
 *      and a primary Ask → Stop button that swaps role while a request
 *      is in flight.
 *
 * Submit/cancel/streaming live in the page; this component delegates to
 * the page-supplied handlers.
 *
 * There is deliberately no specialisation filter here: agent retrieval
 * (agents/knowledge-agent/src/retrieve.py) can only scope by documentTitle
 * (Firestore `in`, max 30), so "+ Add specialisation" chips were never
 * sent and did nothing. Re-add them only together with a server-side
 * specialisation filter.
 */
const DEEP_MODE_OPTIONS: { value: AnswerMode; label: string }[] = [
  { value: 'quick', label: 'Quick' },
  { value: 'deep_business_analyst', label: 'Business Analyst' },
  { value: 'deep_developer', label: 'Developer' },
  { value: 'deep_customer_success', label: 'Customer Success' },
  { value: 'deep_marketing', label: 'Marketing' },
  { value: 'deep_quality_analyst', label: 'Quality Analyst' },
  { value: 'deep_legal', label: 'Legal' },
];

const ATTACH_ACCEPT =
  '.txt,.md,.csv,.json,.yaml,.yml,.ts,.tsx,.js,.jsx,.py,.cs,.sql,.go,.rb,.java,.sh,.html,.xml,' +
  '.xlsx,.xls,.pdf,.docx,' +
  'text/plain,text/markdown,text/csv,application/json,text/yaml,application/x-yaml,' +
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel,' +
  'application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document';

const ATTACH_ALLOWED_EXTS = new Set([
  'txt',
  'md',
  'csv',
  'json',
  'yaml',
  'yml',
  'ts',
  'tsx',
  'js',
  'jsx',
  'py',
  'cs',
  'sql',
  'go',
  'rb',
  'java',
  'sh',
  'html',
  'xml',
  'xlsx',
  'xls',
  'pdf',
  'docx',
]);

const ATTACH_ALLOWED_MIMES = new Set([
  'text/plain',
  'text/markdown',
  'text/csv',
  'application/json',
  'text/yaml',
  'application/x-yaml',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.ms-excel',
  'application/pdf',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
]);

const XLSX_EXTS = new Set(['xlsx', 'xls']);
const PDF_EXTS = new Set(['pdf']);
const DOCX_EXTS = new Set(['docx']);

/**
 * Convert an .xlsx / .xls file to a plain-text representation the agent
 * can read. Each sheet renders as a `## Sheet: <name>` header followed
 * by the sheet's CSV. Empty sheets are skipped. Cell formula strings
 * are evaluated by SheetJS where possible; calculated values are
 * preferred over the formula expression itself.
 */
async function xlsxToText(file: File): Promise<string> {
  const buf = await file.arrayBuffer();
  const wb = XLSX.read(buf, { type: 'array' });
  const out: string[] = [];
  for (const name of wb.SheetNames) {
    const sheet = wb.Sheets[name];
    if (!sheet) continue;
    const csv = XLSX.utils.sheet_to_csv(sheet, { blankrows: false }).trim();
    if (!csv) continue;
    out.push(`## Sheet: ${name}\n${csv}`);
  }
  return out.join('\n\n');
}

/**
 * Extract plain text from a .pdf in the browser via pdfjs-dist. Walks
 * each page's text content and concatenates with a page separator so
 * the agent can cite "page 3" in its answer. Image-only (scanned) PDFs
 * extract to empty/near-empty text and trip the empty-text check below.
 */
async function pdfToText(file: File): Promise<string> {
  const pdfjs = await import('pdfjs-dist');
  // Workerless mode — bundle the worker inline. Vite handles the URL.
  const worker = await import('pdfjs-dist/build/pdf.worker.mjs?url');
  pdfjs.GlobalWorkerOptions.workerSrc = worker.default;
  const data = await file.arrayBuffer();
  const doc = await pdfjs.getDocument({ data }).promise;
  const pages: string[] = [];
  for (let i = 1; i <= doc.numPages; i++) {
    const page = await doc.getPage(i);
    const content = await page.getTextContent();
    const text = content.items
      .map((it) => ('str' in it ? it.str : ''))
      .filter(Boolean)
      .join(' ');
    if (text.trim()) pages.push(`## Page ${i}\n${text.trim()}`);
  }
  return pages.join('\n\n');
}

/**
 * Extract plain text from a .docx via mammoth. Strips Word styling but
 * preserves paragraph structure as newlines. Tables flatten to TSV-ish
 * text. Images are dropped.
 */
async function docxToText(file: File): Promise<string> {
  const mammoth = await import('mammoth');
  const buf = await file.arrayBuffer();
  const result = await mammoth.extractRawText({ arrayBuffer: buf });
  return (result.value ?? '').trim();
}

function CompoundChatInput({
  inputRef,
  draft,
  setDraft,
  pending,
  onSubmit,
  onCancel,
  answerMode,
  setAnswerMode,
  citationDepth,
  setCitationDepth,
  modelId,
  setModelId,
  showModelPicker,
  allowAdminModels,
  scopeTitles,
  onOpenScopePicker,
  totalDocsInScope,
  hasTurns,
  attachment,
  setAttachment,
  attachError,
  setAttachError,
  canvasEnabled,
  canvasAvailable,
  canvasNextMode,
  setCanvasNextMode,
  canvasNextStarter,
  setCanvasNextStarter,
}: {
  /** Page-owned handle on the textarea, so the page can focus it. */
  inputRef: React.RefObject<HTMLTextAreaElement>;
  draft: string;
  setDraft: (next: string) => void;
  pending: boolean;
  onSubmit: () => void;
  onCancel: () => void;
  answerMode: AnswerMode;
  setAnswerMode: (next: AnswerMode) => void;
  citationDepth: CitationDepth;
  setCitationDepth: (next: CitationDepth) => void;
  modelId: AgentModelId;
  setModelId: (next: AgentModelId) => void;
  showModelPicker: boolean;
  /** Whether the viewer may pick admin-only models (Opus). Non-admins get the
   *  picker but without those entries. */
  allowAdminModels: boolean;
  scopeTitles: string[] | null;
  onOpenScopePicker: () => void;
  totalDocsInScope: number | null;
  hasTurns: boolean;
  attachment: AttachmentState | null;
  setAttachment: (next: AttachmentState | null) => void;
  attachError: string | null;
  setAttachError: (next: string | null) => void;
  /** Whether the canvas feature is enabled for this caller. False hides
   *  the Canvas button entirely (consistent with the pane gating). */
  canvasEnabled: boolean;
  /** Whether a canvas already exists for the active chat. Drives the
   *  click behaviour: if true, button toggles update-mode; if false,
   *  it opens the starter picker. */
  canvasAvailable: boolean;
  /** Next-turn dispatch state. null = chat-mode; 'create' / 'update'
   *  routes the next ask to the canvas flow. */
  canvasNextMode: 'create' | 'update' | null;
  setCanvasNextMode: (next: 'create' | 'update' | null) => void;
  canvasNextStarter: CanvasStarterTemplate | null;
  setCanvasNextStarter: (next: CanvasStarterTemplate | null) => void;
}): JSX.Element {
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const onFilePicked = async (e: React.ChangeEvent<HTMLInputElement>): Promise<void> => {
    setAttachError(null);
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const lower = file.name.toLowerCase();
      const dot = lower.lastIndexOf('.');
      const ext = dot >= 0 ? lower.slice(dot + 1) : '';
      const mime = (file.type || '').toLowerCase();
      const mimeOk = mime ? ATTACH_ALLOWED_MIMES.has(mime) || mime.startsWith('text/') : false;
      const extOk = ATTACH_ALLOWED_EXTS.has(ext);
      if (!mimeOk && !extOk) {
        setAttachError(
          'Unsupported file type. Use TXT, MD, CSV, JSON, YAML, XLSX, DOCX, PDF, or a code file.',
        );
        return;
      }
      const isXlsx = XLSX_EXTS.has(ext);
      const isPdf = PDF_EXTS.has(ext);
      const isDocx = DOCX_EXTS.has(ext);
      const kind: AttachmentKind = isXlsx ? 'excel' : isPdf ? 'pdf' : isDocx ? 'word' : 'text';
      // Two gates (limits + copy in lib/chat-attachment-limits): the raw
      // file size, then the size of the text we would actually send —
      // binary docs can be large raw yet extract to little text.
      const tooBig = rawSizeError(kind, file.size);
      if (tooBig) {
        setAttachError(tooBig);
        return;
      }

      let text: string;
      if (isXlsx) {
        text = await xlsxToText(file);
      } else if (isPdf) {
        text = await pdfToText(file);
      } else if (isDocx) {
        text = await docxToText(file);
      } else {
        text = await file.text();
      }
      const tooMuchText = extractedTextError(kind, text);
      if (tooMuchText) {
        setAttachError(tooMuchText);
        return;
      }
      if (text.trim().length === 0) {
        setAttachError('The file is empty or contains no extractable text.');
        return;
      }
      setAttachment({
        filename: file.name,
        mimeType:
          mime ||
          (isXlsx
            ? 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
            : extOk
              ? `text/${ext}`
              : 'text/plain'),
        contentText: text,
        sizeBytes: file.size,
      });
    } catch (err) {
      setAttachError(
        err instanceof Error
          ? `Could not read the file: ${err.message}`
          : 'Could not read the file.',
      );
    } finally {
      // Reset so the same file can be re-picked after removal.
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const [deepPickerOpen, setDeepPickerOpen] = useState(false);
  const [depthPickerOpen, setDepthPickerOpen] = useState(false);
  const [modelPickerOpen, setModelPickerOpen] = useState(false);
  // Canvas starter picker (issue #3). Opens when the user clicks the
  // Canvas button on a chat that doesn't have a canvas yet — the picker
  // collects which starter template to seed the first version with.
  const [canvasStarterOpen, setCanvasStarterOpen] = useState(false);
  const deepWrapRef = useRef<HTMLDivElement | null>(null);
  const depthWrapRef = useRef<HTMLDivElement | null>(null);
  const modelWrapRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!deepPickerOpen) return;
    const onClick = (e: MouseEvent): void => {
      if (deepWrapRef.current && !deepWrapRef.current.contains(e.target as Node)) {
        setDeepPickerOpen(false);
      }
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [deepPickerOpen]);

  useEffect(() => {
    if (!depthPickerOpen) return;
    const onClick = (e: MouseEvent): void => {
      if (depthWrapRef.current && !depthWrapRef.current.contains(e.target as Node)) {
        setDepthPickerOpen(false);
      }
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [depthPickerOpen]);

  useEffect(() => {
    if (!modelPickerOpen) return;
    const onClick = (e: MouseEvent): void => {
      if (modelWrapRef.current && !modelWrapRef.current.contains(e.target as Node)) {
        setModelPickerOpen(false);
      }
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [modelPickerOpen]);

  const scopeLabel =
    scopeTitles && scopeTitles.length > 0
      ? `${scopeTitles.length} document${scopeTitles.length === 1 ? '' : 's'}`
      : 'All documents';
  const deepActive = answerMode !== 'quick';
  const deepLabel = deepActive
    ? `Deep · ${DEEP_MODE_OPTIONS.find((o) => o.value === answerMode)?.label ?? ''}`
    : 'Deep mode';
  const depthActive = citationDepth !== 'balanced';
  const depthLabel = depthActive
    ? `Citation depth: ${
        CITATION_DEPTH_OPTIONS.find((o) => o.value === citationDepth)?.label ?? ''
      }`
    : 'Citation depth';
  const modelActive = modelId !== DEFAULT_MODEL_ID;
  const modelLabel = modelActive
    ? `Model: ${MODEL_OPTIONS.find((o) => o.value === modelId)?.label ?? ''}`
    : 'Model';

  const askDisabled = !pending && !draft.trim();

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        if (pending) return;
        onSubmit();
      }}
      className="mt-6 rounded-lg border border-nebula-border-soft bg-nebula-surface shadow-card"
    >
      {/* Row 1 — scope dropdown + count */}
      <div className="flex flex-wrap items-center gap-2 px-4 py-3">
        <button
          type="button"
          onClick={onOpenScopePicker}
          className="inline-flex items-center gap-1.5 rounded-full border border-nebula-border bg-nebula-surface px-2.5 py-1 text-[11px] font-medium text-nebula-fg-2 hover:border-nebula-secondary hover:bg-nebula-tint"
          title="Restrict the search to specific documents"
        >
          <FilesIcon size={12} />
          {scopeLabel}
          <ChevronDownIcon size={12} />
        </button>
        <div className="ml-auto text-[11px] text-nebula-fg-3">
          {totalDocsInScope !== null
            ? `${totalDocsInScope} document${totalDocsInScope === 1 ? '' : 's'} in scope`
            : ''}
        </div>
      </div>

      {/* Row 2 — textarea */}
      <div className="border-t border-nebula-border-soft px-4 py-3">
        <textarea
          ref={inputRef}
          aria-label="Ask the Knowledge Agent"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onFocus={() => {
            if (attachError) setAttachError(null);
          }}
          onKeyDown={(e) => {
            // Cmd/Ctrl+Enter and bare Enter both submit (Shift+Enter
            // inserts a newline). Preserves the previous behaviour.
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              if (!pending && draft.trim()) onSubmit();
            }
          }}
          disabled={pending}
          maxLength={8000}
          rows={3}
          placeholder="What would you like to know?"
          className="block w-full resize-none border-0 bg-transparent text-sm text-nebula-fg-1 placeholder:text-nebula-fg-4 focus:outline-none disabled:opacity-60"
        />
      </div>

      {/* Row 3 — toolbar */}
      <div className="flex flex-wrap items-center gap-1 border-t border-nebula-border-soft px-4 py-3">
        <div ref={deepWrapRef} className="relative">
          <button
            type="button"
            onClick={() => setDeepPickerOpen((v) => !v)}
            disabled={pending}
            className={
              deepActive
                ? 'inline-flex items-center gap-1.5 rounded-md bg-nebula-tint px-2.5 py-1.5 text-xs font-medium text-nebula-primary disabled:opacity-50'
                : 'inline-flex items-center gap-1.5 rounded-md px-2.5 py-1.5 text-xs font-medium text-nebula-fg-2 hover:bg-nebula-tint disabled:opacity-50'
            }
            title="Choose answer depth"
          >
            <BrainIcon size={14} /> {deepLabel}
          </button>
          {deepPickerOpen && (
            <div className="absolute left-0 bottom-full z-20 mb-1 w-56 overflow-hidden rounded-md border border-nebula-border bg-nebula-surface p-1 shadow-card">
              {DEEP_MODE_OPTIONS.map((opt) => {
                const active = answerMode === opt.value;
                return (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => {
                      setAnswerMode(opt.value);
                      setDeepPickerOpen(false);
                    }}
                    className={
                      active
                        ? 'block w-full rounded bg-nebula-tint px-2 py-1.5 text-left text-xs font-medium text-nebula-primary'
                        : 'block w-full rounded px-2 py-1.5 text-left text-xs text-nebula-fg-1 hover:bg-nebula-tint'
                    }
                  >
                    {opt.label}
                  </button>
                );
              })}
            </div>
          )}
        </div>
        <input
          ref={fileInputRef}
          type="file"
          accept={ATTACH_ACCEPT}
          className="hidden"
          onChange={(e) => {
            void onFilePicked(e);
          }}
        />
        <button
          type="button"
          onClick={() => fileInputRef.current?.click()}
          disabled={pending || attachment !== null}
          className="inline-flex items-center gap-1.5 rounded-md px-2.5 py-1.5 text-xs font-medium text-nebula-fg-2 hover:bg-nebula-tint disabled:opacity-50"
          title={
            attachment ? 'Remove the current attachment to pick another file' : ATTACH_LIMITS_HINT
          }
        >
          <AttachFileIcon size={14} /> Attach
        </button>
        {attachment && (
          <span
            className="inline-flex items-center gap-1 rounded-md border border-nebula-border bg-nebula-tint px-2 py-1 font-mono text-xs text-nebula-fg-1"
            title={`${attachment.filename} (${attachment.sizeBytes} bytes)`}
          >
            <AttachFileIcon size={11} />
            {attachment.filename} ({Math.max(1, Math.round(attachment.sizeBytes / 1024))} KB)
            <button
              type="button"
              onClick={() => {
                setAttachment(null);
                setAttachError(null);
              }}
              aria-label={`Remove ${attachment.filename}`}
              className="ml-0.5 rounded text-nebula-fg-2 hover:text-nebula-fg-1"
            >
              <XIcon size={11} />
            </button>
          </span>
        )}
        <div ref={depthWrapRef} className="relative">
          <button
            type="button"
            onClick={() => setDepthPickerOpen((v) => !v)}
            disabled={pending}
            className={
              depthActive
                ? 'inline-flex items-center gap-1.5 rounded-md bg-nebula-tint px-2.5 py-1.5 text-xs font-medium text-nebula-primary disabled:opacity-50'
                : 'inline-flex items-center gap-1.5 rounded-md px-2.5 py-1.5 text-xs font-medium text-nebula-fg-2 hover:bg-nebula-tint disabled:opacity-50'
            }
            title="Adjust how many sources to retrieve per question"
          >
            <TuneIcon size={14} /> {depthLabel}
          </button>
          {depthPickerOpen && (
            <div className="absolute left-0 bottom-full z-20 mb-1 w-60 overflow-hidden rounded-md border border-nebula-border bg-nebula-surface p-1 shadow-card">
              {CITATION_DEPTH_OPTIONS.map((opt) => {
                const active = citationDepth === opt.value;
                return (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => {
                      setCitationDepth(opt.value);
                      setDepthPickerOpen(false);
                    }}
                    className={
                      active
                        ? 'block w-full rounded bg-nebula-tint px-2 py-1.5 text-left text-xs text-nebula-primary'
                        : 'block w-full rounded px-2 py-1.5 text-left text-xs text-nebula-fg-1 hover:bg-nebula-tint'
                    }
                  >
                    <span className="font-medium">{opt.label}</span>
                    <span className="ml-1 text-nebula-fg-3">— {opt.hint}</span>
                  </button>
                );
              })}
            </div>
          )}
        </div>
        {/* Answer-model picker — open to every user; admin-only models
            (Opus) are filtered out of the list for non-admins. Active
            (non-default) state mirrors the citation-depth picker styling so
            the toolbar stays visually consistent. */}
        {showModelPicker && (
          <div ref={modelWrapRef} className="relative">
            <button
              type="button"
              onClick={() => setModelPickerOpen((v) => !v)}
              disabled={pending}
              className={
                modelActive
                  ? 'inline-flex items-center gap-1.5 rounded-md bg-nebula-tint px-2.5 py-1.5 text-xs font-medium text-nebula-primary disabled:opacity-50'
                  : 'inline-flex items-center gap-1.5 rounded-md px-2.5 py-1.5 text-xs font-medium text-nebula-fg-2 hover:bg-nebula-tint disabled:opacity-50'
              }
              title="Choose which AI model writes the answer"
            >
              <BrainIcon size={14} /> {modelLabel}
              {modelActive && (
                <span className="ml-1 inline-flex items-center rounded-full bg-nebula-primary px-1.5 py-0.5 font-mono text-[10px] font-medium text-white">
                  {MODEL_BADGE_LABELS[modelId] ?? modelId}
                </span>
              )}
            </button>
            {modelPickerOpen && (
              <div className="absolute left-0 bottom-full z-20 mb-1 w-64 overflow-hidden rounded-md border border-nebula-border bg-nebula-surface p-1 shadow-card">
                {MODEL_OPTIONS.filter((opt) => canPickModel(opt.value, allowAdminModels)).map(
                  (opt) => {
                    const active = modelId === opt.value;
                    return (
                      <button
                        key={opt.value}
                        type="button"
                        onClick={() => {
                          setModelId(opt.value);
                          setModelPickerOpen(false);
                        }}
                        className={
                          active
                            ? 'block w-full rounded bg-nebula-tint px-2 py-1.5 text-left text-xs font-medium text-nebula-primary'
                            : 'block w-full rounded px-2 py-1.5 text-left text-xs text-nebula-fg-1 hover:bg-nebula-tint'
                        }
                      >
                        {opt.label}
                      </button>
                    );
                  },
                )}
              </div>
            )}
          </div>
        )}
        {canvasEnabled && (
          <div className="relative">
            <button
              type="button"
              disabled={pending}
              onClick={() => {
                if (canvasNextMode) {
                  // Already toggled — clear next-turn dispatch.
                  setCanvasNextMode(null);
                  setCanvasNextStarter(null);
                  setCanvasStarterOpen(false);
                } else if (canvasAvailable) {
                  // Canvas exists → next turn rewrites it.
                  setCanvasNextMode('update');
                } else {
                  // No canvas yet → open the starter picker to choose
                  // what template to create with.
                  setCanvasStarterOpen(true);
                }
              }}
              className={
                canvasNextMode
                  ? 'inline-flex items-center gap-1.5 rounded-md bg-nebula-primary px-2.5 py-1.5 text-xs font-medium text-white disabled:opacity-50'
                  : 'inline-flex items-center gap-1.5 rounded-md px-2.5 py-1.5 text-xs font-medium text-nebula-fg-2 hover:bg-nebula-tint disabled:opacity-50'
              }
              title={
                canvasNextMode === 'update'
                  ? 'Next turn will update the canvas. Click to cancel.'
                  : canvasNextMode === 'create'
                    ? 'Next turn will create a canvas. Click to cancel.'
                    : canvasAvailable
                      ? 'Send the next turn to the canvas'
                      : 'Start a canvas with the next turn'
              }
            >
              <FilesIcon size={14} />
              {canvasNextMode === 'update'
                ? 'Canvas: Update'
                : canvasNextMode === 'create'
                  ? `Canvas: New${canvasNextStarter && canvasNextStarter !== 'blank' ? ` (${canvasNextStarter.replace(/_/g, ' ')})` : ''}`
                  : 'Canvas'}
            </button>
            {canvasStarterOpen && (
              <CanvasStarterPicker
                onChoose={(template) => {
                  setCanvasNextMode('create');
                  setCanvasNextStarter(template);
                  setCanvasStarterOpen(false);
                }}
                onCancel={() => setCanvasStarterOpen(false)}
              />
            )}
          </div>
        )}
        <div className="ml-auto">
          {pending ? (
            <button
              type="button"
              onClick={onCancel}
              className="inline-flex items-center gap-1.5 rounded-md bg-nebula-primary px-4 py-2 text-sm font-medium text-white hover:bg-nebula-secondary"
              title="Stop generating"
            >
              Stop
              <StopCircleIcon size={16} />
            </button>
          ) : (
            <button
              type="submit"
              disabled={askDisabled}
              className="inline-flex items-center gap-1.5 rounded-md bg-nebula-primary px-4 py-2 text-sm font-medium text-white hover:bg-nebula-secondary disabled:cursor-not-allowed disabled:opacity-50"
            >
              {hasTurns ? 'Follow-up' : 'Ask'}
              <ArrowForwardIcon size={16} />
            </button>
          )}
        </div>
      </div>
    </form>
  );
}

function ScopePickerModal({
  currentSelection,
  onApply,
  onClose,
}: {
  currentSelection: string[] | null;
  onApply: (next: string[] | null) => void;
  onClose: () => void;
}): JSX.Element {
  const [docs, setDocs] = useState<DocumentSummary[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState('');
  // Local working set — applied on save, discarded on cancel.
  const [selected, setSelected] = useState<Set<string>>(() => new Set(currentSelection ?? []));

  useEffect(() => {
    let alive = true;
    apiGet<DocumentsResponse>('/api/v1/agent/documents')
      .then((d) => {
        if (alive) setDocs(d.documents);
      })
      .catch((err) => {
        if (alive)
          setError(err instanceof ApiError ? err.message : 'Could not load document list.');
      });
    return () => {
      alive = false;
    };
  }, []);

  const toggle = (title: string): void => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(title)) next.delete(title);
      else next.add(title);
      return next;
    });
  };

  const filtered = (docs ?? []).filter((d) => {
    if (!filter.trim()) return true;
    const q = filter.toLowerCase();
    return d.documentTitle.toLowerCase().includes(q) || d.author.toLowerCase().includes(q);
  });

  const handleApply = (): void => {
    if (selected.size === 0) {
      onApply(null); // empty selection == "all docs"
    } else {
      onApply(Array.from(selected));
    }
  };

  const handleSelectAll = (): void => setSelected(new Set());

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-ink-900/40 px-4"
      onClick={onClose}
    >
      <div
        className="flex max-h-[85vh] w-full max-w-2xl flex-col rounded-2xl bg-white shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between border-b border-ring2/70 px-6 pt-5 pb-3">
          <div>
            <h2 className="text-base font-semibold text-ink-900">Scope</h2>
            <p className="mt-1 text-xs text-ink-900/55">
              Select specific documents to restrict your question to. No selection = all documents
              (default).
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-full p-1 text-ink-900/55 hover:bg-ink-900/5 hover:text-ink-900"
            aria-label="Close"
          >
            <XIcon size={18} />
          </button>
        </div>

        <div className="border-b border-ring2/70 px-6 py-3">
          <input
            type="text"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            placeholder="Filter by title or author…"
            className="w-full rounded-lg border border-ring2 bg-white px-3 py-2 text-sm placeholder:text-ink-900/35 focus:border-brand-700 focus:outline-none"
          />
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-3">
          {error && (
            <div className="rounded-xl border border-amber-300/70 bg-amber-50 px-4 py-3 text-sm text-amber-900">
              {error}
            </div>
          )}
          {!docs && !error && <div className="text-sm text-ink-900/55">Loading documents…</div>}
          {docs && filtered.length === 0 && (
            <div className="text-sm text-ink-900/55">No documents found for "{filter}".</div>
          )}
          {docs && filtered.length > 0 && (
            <ul className="divide-y divide-ring2/50">
              {filtered.map((d) => {
                const isSelected = selected.has(d.documentTitle);
                return (
                  <li key={d.sourceFile}>
                    <button
                      type="button"
                      onClick={() => toggle(d.documentTitle)}
                      className={`flex w-full items-start gap-3 py-2 text-left ${
                        isSelected ? 'text-ink-900' : 'text-ink-900/85 hover:text-ink-900'
                      }`}
                    >
                      <span
                        className={`mt-0.5 inline-flex h-4 w-4 shrink-0 items-center justify-center rounded border ${
                          isSelected
                            ? 'border-brand-700 bg-brand-700 text-white'
                            : 'border-ring2 bg-white'
                        }`}
                      >
                        {isSelected && <CheckIcon size={11} />}
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className="block truncate text-sm font-medium">
                          {d.documentTitle}
                        </span>
                        <span className="block truncate text-[11px] text-ink-900/55">
                          {d.author}
                        </span>
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        <div className="flex items-center justify-between gap-3 border-t border-ring2/70 px-6 py-3">
          <div className="text-[11px] text-ink-900/55">
            {selected.size === 0
              ? 'No selection — all documents (default)'
              : `${selected.size} document${selected.size === 1 ? '' : 's'} selected`}
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={handleSelectAll}
              className="rounded-lg px-3 py-1.5 text-xs text-ink-900/65 hover:text-ink-900"
            >
              Clear all
            </button>
            <button
              type="button"
              onClick={onClose}
              className="rounded-lg border border-ring2 bg-white px-3 py-1.5 text-xs text-ink-900/70 hover:text-ink-900"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleApply}
              className="rounded-lg bg-brand-800 px-3 py-1.5 text-xs font-medium text-white hover:bg-brand-700"
            >
              Apply
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * Three bouncing dots — drop-in typing indicator. Uses Tailwind's built-in
 * `animate-bounce` with staggered `animation-delay`. The spans share a
 * single keyframe so the cadence is in lockstep across the dots.
 */
function TypingDots(): JSX.Element {
  return (
    <span aria-label="Knowledge Agent is thinking" className="inline-flex items-center gap-1">
      <span
        className="h-1.5 w-1.5 animate-bounce rounded-full bg-brand-700"
        style={{ animationDelay: '0ms' }}
      />
      <span
        className="h-1.5 w-1.5 animate-bounce rounded-full bg-brand-700"
        style={{ animationDelay: '150ms' }}
      />
      <span
        className="h-1.5 w-1.5 animate-bounce rounded-full bg-brand-700"
        style={{ animationDelay: '300ms' }}
      />
    </span>
  );
}
